// src/types.ts
import { AddressType } from "@phantom/client";

// src/providers/injected/index.ts
import { AddressType as AddressType2 } from "@phantom/client";

// src/debug.ts
var DebugLevel = /* @__PURE__ */ ((DebugLevel2) => {
  DebugLevel2[DebugLevel2["ERROR"] = 0] = "ERROR";
  DebugLevel2[DebugLevel2["WARN"] = 1] = "WARN";
  DebugLevel2[DebugLevel2["INFO"] = 2] = "INFO";
  DebugLevel2[DebugLevel2["DEBUG"] = 3] = "DEBUG";
  return DebugLevel2;
})(DebugLevel || {});
var Debug = class {
  constructor() {
    this.level = 0 /* ERROR */;
    this.enabled = false;
  }
  static getInstance() {
    if (!Debug.instance) {
      Debug.instance = new Debug();
    }
    return Debug.instance;
  }
  setCallback(callback) {
    this.callback = callback;
  }
  setLevel(level) {
    this.level = level;
  }
  enable() {
    this.enabled = true;
  }
  disable() {
    this.enabled = false;
  }
  writeLog(level, category, message, data) {
    if (!this.enabled || level > this.level) {
      return;
    }
    const debugMessage = {
      timestamp: Date.now(),
      level,
      category,
      message,
      data
    };
    if (this.callback) {
      this.callback(debugMessage);
    }
  }
  error(category, message, data) {
    this.writeLog(0 /* ERROR */, category, message, data);
  }
  warn(category, message, data) {
    this.writeLog(1 /* WARN */, category, message, data);
  }
  info(category, message, data) {
    this.writeLog(2 /* INFO */, category, message, data);
  }
  debug(category, message, data) {
    this.writeLog(3 /* DEBUG */, category, message, data);
  }
  log(category, message, data) {
    this.writeLog(3 /* DEBUG */, category, message, data);
  }
};
var debug = Debug.getInstance();
var DebugCategory = {
  BROWSER_SDK: "BrowserSDK",
  PROVIDER_MANAGER: "ProviderManager",
  EMBEDDED_PROVIDER: "EmbeddedProvider",
  INJECTED_PROVIDER: "InjectedProvider",
  PHANTOM_CONNECT_AUTH: "PhantomConnectAuth",
  JWT_AUTH: "JWTAuth",
  STORAGE: "Storage",
  SESSION: "Session"
};

// src/wallets/discovery.ts
import { AddressType as ClientAddressType } from "@phantom/client";
import { isPhantomExtensionInstalled } from "@phantom/browser-injected-sdk";
import { createPhantom, createExtensionPlugin } from "@phantom/browser-injected-sdk";
import { createSolanaPlugin } from "@phantom/browser-injected-sdk/solana";
import { createEthereumPlugin } from "@phantom/browser-injected-sdk/ethereum";
import { createAutoConfirmPlugin } from "@phantom/browser-injected-sdk/auto-confirm";
function generateWalletIdFromEIP6963(info) {
  if (info.rdns) {
    return info.rdns.split(".").reverse().join("-");
  }
  return info.name.toLowerCase().replace(/\s+/g, "-");
}
function generateWalletIdFromName(name) {
  return name.toLowerCase().replace(/\s+/g, "-");
}
function processEIP6963Providers(providers) {
  const wallets = [];
  debug.log(DebugCategory.BROWSER_SDK, "Processing EIP-6963 providers", {
    providerCount: providers.size,
    providerNames: Array.from(providers.values()).map((d) => d.info.name)
  });
  for (const [, detail] of providers) {
    const { info, provider } = detail;
    const isPhantom = info.name.toLowerCase().includes("phantom") || info.rdns && (info.rdns.toLowerCase().includes("phantom") || info.rdns.toLowerCase() === "app.phantom");
    if (isPhantom) {
      debug.log(DebugCategory.BROWSER_SDK, "Skipping Phantom from EIP-6963", { name: info.name, rdns: info.rdns });
      continue;
    }
    const walletId = generateWalletIdFromEIP6963(info);
    debug.log(DebugCategory.BROWSER_SDK, "Discovered EIP-6963 wallet", {
      walletId,
      walletName: info.name,
      rdns: info.rdns
    });
    wallets.push({
      id: walletId,
      name: info.name,
      icon: info.icon,
      addressTypes: [ClientAddressType.ethereum],
      providers: {
        // EIP-6963 provider implements EIP-1193 interface (IEthereumChain)
        ethereum: provider
      },
      rdns: info.rdns,
      // Store rdns for potential future matching
      discovery: "eip6963"
    });
  }
  debug.log(DebugCategory.BROWSER_SDK, "EIP-6963 discovery completed", {
    discoveredCount: wallets.length,
    walletIds: wallets.map((w) => w.id)
  });
  return wallets;
}
function discoverEthereumWallets() {
  return new Promise((resolve) => {
    const discoveredProviders = /* @__PURE__ */ new Map();
    if (typeof window === "undefined") {
      resolve([]);
      return;
    }
    const handleAnnounce = (event) => {
      const detail = event.detail;
      if (detail?.info && detail?.provider) {
        discoveredProviders.set(detail.info.uuid, detail);
      }
    };
    window.addEventListener("eip6963:announceProvider", handleAnnounce);
    window.dispatchEvent(new Event("eip6963:requestProvider"));
    const processProviders = () => {
      const wallets = processEIP6963Providers(discoveredProviders);
      window.removeEventListener("eip6963:announceProvider", handleAnnounce);
      resolve(wallets);
    };
    setTimeout(processProviders, 400);
  });
}
async function discoverSolanaWallets() {
  const wallets = [];
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    debug.log(DebugCategory.BROWSER_SDK, "Wallet Standard discovery skipped (not in browser environment)");
    return wallets;
  }
  const registeredWalletsSet = /* @__PURE__ */ new Set();
  let cachedWalletsArray;
  function addRegisteredWallet(wallet) {
    cachedWalletsArray = void 0;
    registeredWalletsSet.add(wallet);
    const featureKeys = wallet.features ? Object.keys(wallet.features) : [];
    debug.log(DebugCategory.BROWSER_SDK, "Wallet registered", {
      name: wallet.name,
      chains: wallet.chains,
      featureKeys,
      totalWallets: registeredWalletsSet.size
    });
  }
  function removeRegisteredWallet(wallet) {
    cachedWalletsArray = void 0;
    registeredWalletsSet.delete(wallet);
  }
  function getRegisteredWallets() {
    if (!cachedWalletsArray) {
      cachedWalletsArray = [...registeredWalletsSet];
    }
    return cachedWalletsArray;
  }
  function register(...wallets2) {
    wallets2 = wallets2.filter((wallet) => !registeredWalletsSet.has(wallet));
    if (!wallets2.length) {
      return () => {
      };
    }
    wallets2.forEach((wallet) => addRegisteredWallet(wallet));
    return function unregister() {
      wallets2.forEach((wallet) => removeRegisteredWallet(wallet));
    };
  }
  const registerAPI = Object.freeze({ register });
  const handleRegisterWalletEvent = (event) => {
    const callback = event.detail;
    if (typeof callback === "function") {
      try {
        callback(registerAPI);
      } catch (error) {
        debug.warn(DebugCategory.BROWSER_SDK, "Error calling wallet registration callback", { error });
      }
    }
  };
  try {
    window.addEventListener("wallet-standard:register-wallet", handleRegisterWalletEvent);
  } catch (error) {
    debug.warn(DebugCategory.BROWSER_SDK, "Could not add register-wallet event listener", { error });
  }
  class AppReadyEvent extends Event {
    constructor(api) {
      super("wallet-standard:app-ready", {
        bubbles: false,
        cancelable: false,
        composed: false
      });
      this.detail = api;
    }
  }
  try {
    window.dispatchEvent(new AppReadyEvent(registerAPI));
    debug.log(DebugCategory.BROWSER_SDK, "Dispatched wallet-standard:app-ready event");
  } catch (error) {
    debug.warn(DebugCategory.BROWSER_SDK, "Could not dispatch app-ready event", { error });
  }
  const walletsAPI = {
    getWallets: () => {
      return {
        get: getRegisteredWallets,
        on: (_event, _listener) => {
          return () => {
          };
        },
        register
      };
    }
  };
  if (!navigator.wallets) {
    navigator.wallets = walletsAPI;
  }
  debug.log(DebugCategory.BROWSER_SDK, "Initialized Wallet Standard registry");
  await new Promise((resolve) => setTimeout(resolve, 100));
  const existingWalletsAPI = navigator.wallets || window.wallets;
  if (!existingWalletsAPI || typeof existingWalletsAPI.getWallets !== "function") {
    const logData = {
      hasNavigator: !!navigator,
      hasWindow: typeof window !== "undefined",
      note: "Wallet Standard API not properly initialized"
    };
    debug.log(DebugCategory.BROWSER_SDK, "Wallet Standard API not available", logData);
    return wallets;
  }
  const walletsGetter = existingWalletsAPI.getWallets();
  const getWalletsFn = () => Promise.resolve([...walletsGetter.get()]);
  debug.log(DebugCategory.BROWSER_SDK, "Wallet Standard API detected, starting discovery");
  try {
    let registeredWallets = [];
    let attempts = 0;
    const maxAttempts = 5;
    const initialDelay = 100;
    const eip6963Timeout = 400;
    await new Promise((resolve) => setTimeout(resolve, initialDelay));
    while (attempts < maxAttempts) {
      registeredWallets = await getWalletsFn();
      const logData = {
        attempt: attempts + 1,
        walletCount: registeredWallets.length,
        walletNames: registeredWallets.map((w) => w.name),
        chains: registeredWallets.flatMap((w) => w.chains)
      };
      debug.log(DebugCategory.BROWSER_SDK, `Wallet Standard getWallets attempt ${attempts + 1}`, logData);
      if (registeredWallets.length > 0 || attempts === maxAttempts - 1) {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, initialDelay));
      attempts++;
    }
    const totalWaitTime = initialDelay + attempts * initialDelay;
    if (totalWaitTime < eip6963Timeout) {
      const remainingWait = eip6963Timeout - totalWaitTime;
      await new Promise((resolve) => setTimeout(resolve, remainingWait));
      registeredWallets = await getWalletsFn();
    }
    debug.log(DebugCategory.BROWSER_SDK, "Wallet Standard getWallets final result", {
      walletCount: registeredWallets.length,
      walletNames: registeredWallets.map((w) => w.name),
      attempts: attempts + 1
    });
    for (const wallet of registeredWallets) {
      const supportsSolana = wallet.chains.some((chain) => {
        const chainLower = chain.toLowerCase();
        return chainLower.startsWith("solana:") || chainLower === "solana";
      }) || wallet.features && typeof wallet.features === "object" && Object.keys(wallet.features).some((featureKey) => {
        const featureLower = featureKey.toLowerCase();
        return featureLower.startsWith("solana:");
      });
      if (!supportsSolana) {
        const featureKeys = wallet.features ? Object.keys(wallet.features) : [];
        debug.log(DebugCategory.BROWSER_SDK, "Wallet does not support Solana", {
          walletName: wallet.name,
          chains: wallet.chains,
          featureKeys
        });
        continue;
      }
      if (wallet.name.toLowerCase().includes("phantom")) {
        debug.log(DebugCategory.BROWSER_SDK, "Skipping Phantom from Wallet Standard (handled separately)");
        continue;
      }
      const walletId = generateWalletIdFromName(wallet.name);
      const safeFeatures = wallet.features ? Object.keys(wallet.features) : [];
      debug.log(DebugCategory.BROWSER_SDK, "Discovered Wallet Standard Solana wallet", {
        walletId,
        walletName: wallet.name,
        chains: wallet.chains,
        featureKeys: safeFeatures,
        icon: wallet.icon,
        version: wallet.version,
        accountCount: wallet.accounts?.length || 0
      });
      wallets.push({
        id: walletId,
        name: wallet.name,
        icon: wallet.icon,
        addressTypes: [ClientAddressType.solana],
        providers: {
          // Cast to ISolanaChain - Wallet Standard wallets have compatible methods
          // The InjectedWalletSolanaChain wrapper will handle the actual method calls
          solana: wallet
        },
        discovery: "standard"
      });
    }
  } catch (error) {
    debug.warn(DebugCategory.BROWSER_SDK, "Wallet Standard API error", {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : void 0
    });
  }
  const finalLogData = {
    discoveredCount: wallets.length,
    walletIds: wallets.map((w) => w.id),
    walletNames: wallets.map((w) => w.name)
  };
  debug.log(DebugCategory.BROWSER_SDK, "Wallet Standard Solana discovery completed", finalLogData);
  return wallets;
}
function discoverPhantomWallet(addressTypes) {
  if (typeof window === "undefined") {
    return null;
  }
  if (!isPhantomExtensionInstalled()) {
    return null;
  }
  const plugins = [createExtensionPlugin()];
  if (addressTypes.includes(ClientAddressType.solana)) {
    plugins.push(createSolanaPlugin());
  }
  if (addressTypes.includes(ClientAddressType.ethereum)) {
    plugins.push(createEthereumPlugin());
  }
  plugins.push(createAutoConfirmPlugin());
  const phantomInstance = createPhantom({ plugins });
  return {
    id: "phantom",
    name: "Phantom",
    icon: void 0,
    // Icon will be rendered from icons package in UI components
    addressTypes,
    providers: {
      solana: addressTypes.includes(ClientAddressType.solana) ? phantomInstance.solana : void 0,
      ethereum: addressTypes.includes(ClientAddressType.ethereum) ? phantomInstance.ethereum : void 0
    },
    isPhantom: true,
    phantomInstance,
    discovery: "phantom"
  };
}
async function discoverWallets(addressTypes) {
  const requestedAddressTypes = addressTypes || [];
  debug.log(DebugCategory.BROWSER_SDK, "Starting all wallet discovery methods", {
    addressTypes: requestedAddressTypes
  });
  const [solanaWallets, ethereumWallets] = await Promise.all([discoverSolanaWallets(), discoverEthereumWallets()]);
  const phantomWallet = discoverPhantomWallet(requestedAddressTypes);
  debug.log(DebugCategory.BROWSER_SDK, "All wallet discovery methods completed", {
    phantomFound: !!phantomWallet,
    solanaWalletsCount: solanaWallets.length,
    ethereumWalletsCount: ethereumWallets.length,
    solanaWalletIds: solanaWallets.map((w) => w.id),
    ethereumWalletIds: ethereumWallets.map((w) => w.id)
  });
  const walletMap = /* @__PURE__ */ new Map();
  if (phantomWallet) {
    walletMap.set("phantom", phantomWallet);
  }
  for (const wallet of [...solanaWallets, ...ethereumWallets]) {
    const existing = walletMap.get(wallet.id);
    if (existing) {
      const mergedAddressTypes = Array.from(/* @__PURE__ */ new Set([...existing.addressTypes, ...wallet.addressTypes]));
      const mergedProviders = {
        ...existing.providers,
        ...wallet.providers
      };
      const mergedWallet = {
        ...existing,
        addressTypes: mergedAddressTypes,
        // Prefer icon from the most recent discovery
        icon: wallet.icon || existing.icon,
        providers: mergedProviders
      };
      walletMap.set(wallet.id, mergedWallet);
      debug.log(DebugCategory.BROWSER_SDK, "Merged wallet by ID", {
        walletName: wallet.name,
        walletId: wallet.id,
        existingAddressTypes: existing.addressTypes,
        newAddressTypes: wallet.addressTypes,
        mergedAddressTypes,
        existingProviders: Object.keys(existing.providers || {}),
        newProviders: Object.keys(wallet.providers || {}),
        mergedProviders: Object.keys(mergedProviders)
      });
      debug.log(DebugCategory.BROWSER_SDK, "Merged wallet from multiple discovery methods", {
        walletId: wallet.id,
        walletName: wallet.name,
        existingAddressTypes: existing.addressTypes,
        newAddressTypes: wallet.addressTypes,
        mergedAddressTypes
      });
    } else {
      walletMap.set(wallet.id, wallet);
    }
  }
  return Array.from(walletMap.values());
}

// src/providers/injected/chains/InjectedWalletSolanaChain.ts
import { EventEmitter } from "eventemitter3";
import { Buffer } from "buffer";
var InjectedWalletSolanaChain = class {
  constructor(provider, walletId, walletName) {
    // Expose eventEmitter for testing - allows tests to trigger events directly
    this.eventEmitter = new EventEmitter();
    this._connected = false;
    this._publicKey = null;
    this.provider = provider;
    this.walletId = walletId;
    this.walletName = walletName;
    this.setupEventListeners();
  }
  get connected() {
    return this._connected;
  }
  get publicKey() {
    return this._publicKey;
  }
  async connect(options) {
    debug.log(DebugCategory.INJECTED_PROVIDER, "External wallet Solana connect", {
      walletId: this.walletId,
      walletName: this.walletName,
      onlyIfTrusted: options?.onlyIfTrusted
    });
    try {
      const result = await this.provider.connect(options);
      if (typeof result === "string") {
        this._connected = true;
        this._publicKey = result;
        debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Solana connected", {
          walletId: this.walletId,
          walletName: this.walletName,
          publicKey: result
        });
        return { publicKey: result };
      }
      if (typeof result === "object" && result !== null && "publicKey" in result) {
        this._connected = true;
        this._publicKey = result.publicKey;
        debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Solana connected", {
          walletId: this.walletId,
          walletName: this.walletName,
          publicKey: result.publicKey
        });
        return result;
      }
      if (Array.isArray(result) && result.length > 0) {
        const firstAccount = result[0];
        if (typeof firstAccount === "object" && firstAccount !== null && "address" in firstAccount) {
          this._connected = true;
          this._publicKey = firstAccount.address;
          debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Solana connected", {
            walletId: this.walletId,
            walletName: this.walletName,
            publicKey: firstAccount.address
          });
          return { publicKey: firstAccount.address };
        }
      }
      throw new Error("Unexpected connect result format");
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "External wallet Solana connect failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  async disconnect() {
    debug.log(DebugCategory.INJECTED_PROVIDER, "External wallet Solana disconnect", {
      walletId: this.walletId,
      walletName: this.walletName
    });
    try {
      await this.provider.disconnect();
      this._connected = false;
      this._publicKey = null;
      debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Solana disconnected", {
        walletId: this.walletId,
        walletName: this.walletName
      });
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "External wallet Solana disconnect failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  async signMessage(message) {
    const messageBytes = typeof message === "string" ? new TextEncoder().encode(message) : message;
    const messagePreview = typeof message === "string" ? message.substring(0, 50) : `${messageBytes.length} bytes`;
    debug.log(DebugCategory.INJECTED_PROVIDER, "External wallet Solana signMessage", {
      walletId: this.walletId,
      walletName: this.walletName,
      messagePreview,
      messageLength: messageBytes.length
    });
    try {
      const result = await this.provider.signMessage(message);
      debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Solana signMessage success", {
        walletId: this.walletId,
        walletName: this.walletName,
        signatureLength: result.signature.length
      });
      return {
        signature: result.signature instanceof Uint8Array ? result.signature : new Uint8Array(Buffer.from(result.signature, "base64")),
        publicKey: result.publicKey || this._publicKey || ""
      };
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "External wallet Solana signMessage failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  async signTransaction(transaction) {
    debug.log(DebugCategory.INJECTED_PROVIDER, "External wallet Solana signTransaction", {
      walletId: this.walletId,
      walletName: this.walletName
    });
    try {
      const result = await this.provider.signTransaction(transaction);
      debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Solana signTransaction success", {
        walletId: this.walletId,
        walletName: this.walletName
      });
      return result;
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "External wallet Solana signTransaction failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  async signAndSendTransaction(transaction) {
    debug.log(DebugCategory.INJECTED_PROVIDER, "External wallet Solana signAndSendTransaction", {
      walletId: this.walletId,
      walletName: this.walletName
    });
    try {
      const result = await this.provider.signAndSendTransaction(transaction);
      debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Solana signAndSendTransaction success", {
        walletId: this.walletId,
        walletName: this.walletName,
        signature: result.signature
      });
      return result;
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "External wallet Solana signAndSendTransaction failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  async signAllTransactions(transactions) {
    debug.log(DebugCategory.INJECTED_PROVIDER, "External wallet Solana signAllTransactions", {
      walletId: this.walletId,
      walletName: this.walletName,
      transactionCount: transactions.length
    });
    try {
      const result = await this.provider.signAllTransactions(transactions);
      debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Solana signAllTransactions success", {
        walletId: this.walletId,
        walletName: this.walletName,
        signedCount: result.length
      });
      return result;
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "External wallet Solana signAllTransactions failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  async signAndSendAllTransactions(transactions) {
    debug.log(DebugCategory.INJECTED_PROVIDER, "External wallet Solana signAndSendAllTransactions", {
      walletId: this.walletId,
      walletName: this.walletName,
      transactionCount: transactions.length
    });
    try {
      const result = await this.provider.signAndSendAllTransactions(transactions);
      debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Solana signAndSendAllTransactions success", {
        walletId: this.walletId,
        walletName: this.walletName,
        signatureCount: result.signatures.length
      });
      return result;
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "External wallet Solana signAndSendAllTransactions failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  switchNetwork(_network) {
    return Promise.resolve();
  }
  getPublicKey() {
    return Promise.resolve(this._publicKey);
  }
  isConnected() {
    return this._connected;
  }
  setupEventListeners() {
    if (typeof this.provider.on === "function") {
      this.provider.on("connect", (publicKey) => {
        this._connected = true;
        this._publicKey = publicKey;
        this.eventEmitter.emit("connect", publicKey);
      });
      this.provider.on("disconnect", () => {
        this._connected = false;
        this._publicKey = null;
        this.eventEmitter.emit("disconnect");
      });
      this.provider.on("accountChanged", (publicKey) => {
        this._publicKey = publicKey;
        this._connected = publicKey != null && publicKey.length > 0;
        this.eventEmitter.emit("accountChanged", publicKey);
      });
    }
  }
  on(event, listener) {
    this.eventEmitter.on(event, listener);
  }
  off(event, listener) {
    this.eventEmitter.off(event, listener);
  }
};

// src/providers/injected/chains/WalletStandardSolanaAdapter.ts
import { EventEmitter as EventEmitter2 } from "eventemitter3";
import { deserializeSolanaTransaction } from "@phantom/parsers";
import { Buffer as Buffer2 } from "buffer";
import bs58 from "bs58";
var WalletStandardSolanaAdapter = class {
  constructor(wallet, walletId, walletName) {
    this.eventEmitter = new EventEmitter2();
    this._publicKey = null;
    this.wallet = wallet;
    this.walletId = walletId;
    this.walletName = walletName;
    this.setupEventListeners();
  }
  get connected() {
    return this._publicKey !== null;
  }
  get publicKey() {
    return this._publicKey;
  }
  async connect(_options) {
    try {
      const connectFeature = this.wallet.features["standard:connect"];
      if (!connectFeature) {
        throw new Error("Wallet Standard connect feature not available");
      }
      const connectResult = await connectFeature.connect();
      let accounts;
      if (Array.isArray(connectResult) && connectResult.length > 0) {
        accounts = connectResult;
      } else if (this.wallet.accounts && this.wallet.accounts.length > 0) {
        accounts = Array.from(this.wallet.accounts);
      }
      if (!accounts || accounts.length === 0) {
        throw new Error("No accounts available after connecting to wallet");
      }
      const firstAccount = accounts[0];
      if (!firstAccount) {
        throw new Error("First account is null or undefined");
      }
      let address;
      if (typeof firstAccount === "string") {
        address = firstAccount;
      } else if (typeof firstAccount === "object" && firstAccount !== null) {
        address = firstAccount.address || firstAccount.publicKey?.toString() || (firstAccount.publicKey instanceof Uint8Array ? Buffer2.from(firstAccount.publicKey).toString("hex") : void 0);
      }
      if (!address) {
        throw new Error(
          `Could not extract address from account. Account structure: ${JSON.stringify(firstAccount, null, 2)}`
        );
      }
      this._publicKey = address;
      return { publicKey: address };
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "Wallet Standard Solana connect failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  async disconnect() {
    try {
      const disconnectFeature = this.wallet.features["standard:disconnect"];
      if (disconnectFeature) {
        await disconnectFeature.disconnect();
      }
      this._publicKey = null;
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "Wallet Standard Solana disconnect failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  async signMessage(message) {
    try {
      const signMessageFeature = this.wallet.features["solana:signMessage"];
      if (!signMessageFeature) {
        throw new Error("Wallet Standard signMessage feature not available");
      }
      const messageBytes = typeof message === "string" ? new TextEncoder().encode(message) : message;
      const result = await signMessageFeature.signMessage({
        message: messageBytes,
        account: this.wallet.accounts[0]
      });
      if (!Array.isArray(result) || result.length === 0) {
        throw new Error(`Expected array result from signMessage, got: ${typeof result}`);
      }
      const signedMessageResult = result[0];
      if (!signedMessageResult || !signedMessageResult.signature) {
        throw new Error(`Invalid signMessage result structure: ${JSON.stringify(result)}`);
      }
      const signature = this.parseUint8Array(signedMessageResult.signature);
      if (signature.length === 0) {
        throw new Error(`Signature is empty`);
      }
      const publicKey = signedMessageResult.account?.address || this.wallet.accounts[0]?.address || this._publicKey || "";
      return { signature, publicKey };
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "Wallet Standard Solana signMessage failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  async signTransaction(transaction) {
    try {
      const signTransactionFeature = this.wallet.features["solana:signTransaction"];
      if (!signTransactionFeature) {
        throw new Error("Wallet Standard signTransaction feature not available");
      }
      if (!this.wallet.accounts || this.wallet.accounts.length === 0) {
        throw new Error("No accounts available. Please connect first.");
      }
      const account = this.wallet.accounts[0];
      const serializedTransaction = this.serializeTransaction(transaction);
      const results = await signTransactionFeature.signTransaction({
        transaction: serializedTransaction,
        account
      });
      let transactionData;
      if (Array.isArray(results) && results.length > 0) {
        const firstItem = results[0];
        if (firstItem && typeof firstItem === "object") {
          transactionData = firstItem.signedTransaction || firstItem.transaction;
        }
      } else if (results && typeof results === "object" && !Array.isArray(results)) {
        const resultObj = results;
        transactionData = resultObj.transaction || resultObj.signedTransaction;
      }
      if (!transactionData) {
        throw new Error("No transaction data found in Wallet Standard result");
      }
      const signedBytes = this.parseUint8Array(transactionData);
      if (signedBytes.length === 0) {
        throw new Error("Empty signed transaction returned from Wallet Standard");
      }
      const signedTx = deserializeSolanaTransaction(signedBytes);
      return signedTx;
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "Wallet Standard Solana signTransaction failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  async signAndSendTransaction(transaction) {
    try {
      const signAndSendTransactionFeature = this.wallet.features["solana:signAndSendTransaction"];
      if (!signAndSendTransactionFeature) {
        throw new Error("Wallet Standard signAndSendTransaction feature not available");
      }
      if (!this.wallet.accounts || this.wallet.accounts.length === 0) {
        throw new Error("No accounts available. Please connect first.");
      }
      const account = this.wallet.accounts[0];
      const chain = account.chains?.[0] || "solana:mainnet";
      const serializedTransaction = this.serializeTransaction(transaction);
      const results = await signAndSendTransactionFeature.signAndSendTransaction({
        transaction: serializedTransaction,
        account,
        chain
      });
      let signatureOutput;
      if (Array.isArray(results) && results.length > 0) {
        signatureOutput = results[0];
      } else if (results && typeof results === "object" && !Array.isArray(results)) {
        signatureOutput = results;
      } else {
        throw new Error("Invalid signAndSendTransaction result format");
      }
      if (!signatureOutput.signature) {
        throw new Error("No signature found in signAndSendTransaction result");
      }
      const signatureBytes = this.parseUint8Array(signatureOutput.signature);
      const signature = bs58.encode(signatureBytes);
      return { signature };
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "Wallet Standard Solana signAndSendTransaction failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  async signAllTransactions(transactions) {
    try {
      const signedTransactions = [];
      for (const transaction of transactions) {
        const signedTx = await this.signTransaction(transaction);
        signedTransactions.push(signedTx);
      }
      return signedTransactions;
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "Wallet Standard Solana signAllTransactions failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  async signAndSendAllTransactions(transactions) {
    try {
      const signatures = [];
      for (const transaction of transactions) {
        const result = await this.signAndSendTransaction(transaction);
        signatures.push(result.signature);
      }
      return { signatures };
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "Wallet Standard Solana signAndSendAllTransactions failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  async switchNetwork(_network) {
    return Promise.resolve();
  }
  getPublicKey() {
    return Promise.resolve(this._publicKey);
  }
  isConnected() {
    return this._publicKey !== null;
  }
  /**
   * Set up event listeners for Wallet Standard events
   * Maps Wallet Standard "change" events to "accountChanged" events
   *
   * Note: Wallet Standard only has a "change" event. There are no "connect" or "disconnect" events.
   * Connection/disconnection is indicated by the presence or absence of accounts in the change event.
   */
  setupEventListeners() {
    const eventsFeature = this.wallet.features["standard:events"];
    if (!eventsFeature) {
      debug.log(DebugCategory.INJECTED_PROVIDER, "Wallet Standard events feature not available", {
        walletId: this.walletId,
        walletName: this.walletName
      });
      return;
    }
    debug.log(DebugCategory.INJECTED_PROVIDER, "Setting up Wallet Standard event listeners", {
      walletId: this.walletId,
      walletName: this.walletName
    });
    eventsFeature.on("change", (properties) => {
      debug.log(DebugCategory.INJECTED_PROVIDER, "Wallet Standard change event received", {
        walletId: this.walletId,
        walletName: this.walletName,
        hasAccounts: !!properties.accounts,
        accountCount: properties.accounts?.length || 0
      });
      if (properties.accounts !== void 0) {
        if (properties.accounts.length > 0) {
          const firstAccount = properties.accounts[0];
          const address = this.extractAccountAddress(firstAccount);
          if (address) {
            this._publicKey = address;
            this.eventEmitter.emit("accountChanged", address);
            this.eventEmitter.emit("connect", address);
            debug.log(DebugCategory.INJECTED_PROVIDER, "Emitted accountChanged and connect events", {
              walletId: this.walletId,
              walletName: this.walletName,
              address
            });
          } else {
            this._publicKey = null;
            this.eventEmitter.emit("accountChanged", null);
            debug.log(DebugCategory.INJECTED_PROVIDER, "Emitted accountChanged event (null - invalid account)", {
              walletId: this.walletId,
              walletName: this.walletName
            });
          }
        } else {
          this._publicKey = null;
          this.eventEmitter.emit("accountChanged", null);
          this.eventEmitter.emit("disconnect");
          debug.log(DebugCategory.INJECTED_PROVIDER, "Emitted accountChanged and disconnect events", {
            walletId: this.walletId,
            walletName: this.walletName
          });
        }
      }
    });
  }
  extractAccountAddress(account) {
    return account.address;
  }
  on(event, listener) {
    this.eventEmitter.on(event, listener);
    debug.log(DebugCategory.INJECTED_PROVIDER, "Added event listener", {
      walletId: this.walletId,
      walletName: this.walletName,
      event
    });
  }
  off(event, listener) {
    this.eventEmitter.off(event, listener);
    debug.log(DebugCategory.INJECTED_PROVIDER, "Removed event listener", {
      walletId: this.walletId,
      walletName: this.walletName,
      event
    });
  }
  /**
   * Serialize a transaction to Uint8Array for Wallet Standard API
   */
  serializeTransaction(transaction) {
    if (typeof transaction.serialize === "function") {
      return transaction.serialize({
        requireAllSignatures: false,
        verifySignatures: false
      });
    }
    if (transaction instanceof Uint8Array) {
      return transaction;
    }
    return new Uint8Array(0);
  }
  /**
   * Parse a Uint8Array from various formats
   * Handles: Uint8Array, Array, object with numeric keys (JSON-serialized Uint8Array)
   */
  parseUint8Array(value) {
    if (value instanceof Uint8Array) {
      return value;
    }
    if (Array.isArray(value)) {
      return new Uint8Array(value);
    }
    if (typeof value === "object" && value !== null) {
      const keys = Object.keys(value).map(Number).filter((k) => !isNaN(k) && k >= 0).sort((a, b) => a - b);
      if (keys.length > 0) {
        const maxKey = Math.max(...keys);
        const array = new Uint8Array(maxKey + 1);
        for (const key of keys) {
          array[key] = Number(value[key]) || 0;
        }
        return array;
      }
    }
    return new Uint8Array(0);
  }
};

// src/providers/injected/chains/InjectedWalletEthereumChain.ts
import { EventEmitter as EventEmitter3 } from "eventemitter3";
var InjectedWalletEthereumChain = class {
  constructor(provider, walletId, walletName) {
    // Expose eventEmitter for testing - allows tests to trigger events directly
    this.eventEmitter = new EventEmitter3();
    this._connected = false;
    this._chainId = "0x1";
    this._accounts = [];
    this.provider = provider;
    this.walletId = walletId;
    this.walletName = walletName;
    this.setupEventListeners();
  }
  get connected() {
    return this._connected;
  }
  get chainId() {
    return this._chainId;
  }
  get accounts() {
    return this._accounts;
  }
  async request(args) {
    debug.log(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum request", {
      walletId: this.walletId,
      walletName: this.walletName,
      method: args.method
    });
    try {
      const requiresAuth = [
        "personal_sign",
        "eth_sign",
        "eth_signTypedData",
        "eth_signTypedData_v4",
        "eth_sendTransaction",
        "eth_signTransaction"
      ].includes(args.method);
      if (requiresAuth && (!this._connected || this._accounts.length === 0)) {
        debug.log(DebugCategory.INJECTED_PROVIDER, "Method requires authorization, ensuring connection", {
          walletId: this.walletId,
          walletName: this.walletName,
          method: args.method
        });
        await this.connect();
      }
      const result = await this.provider.request(args);
      debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum request success", {
        walletId: this.walletId,
        walletName: this.walletName,
        method: args.method
      });
      return result;
    } catch (error) {
      if (error?.code === 4100) {
        debug.log(DebugCategory.INJECTED_PROVIDER, "Got 4100 Unauthorized, attempting to re-authorize", {
          walletId: this.walletId,
          walletName: this.walletName,
          method: args.method
        });
        try {
          await this.provider.request({ method: "eth_requestAccounts" });
          const result = await this.provider.request(args);
          debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum request success (after re-auth)", {
            walletId: this.walletId,
            walletName: this.walletName,
            method: args.method
          });
          return result;
        } catch (retryError) {
          debug.error(DebugCategory.INJECTED_PROVIDER, "Failed after re-authorization", {
            walletId: this.walletId,
            walletName: this.walletName,
            method: args.method,
            error: retryError instanceof Error ? retryError.message : String(retryError)
          });
          throw retryError;
        }
      }
      debug.error(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum request failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        method: args.method,
        error: error instanceof Error ? error.message : String(error),
        errorCode: error?.code
      });
      throw error;
    }
  }
  async connect() {
    debug.log(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum connect", {
      walletId: this.walletId,
      walletName: this.walletName
    });
    try {
      const accounts = await this.provider.request({ method: "eth_requestAccounts" });
      this._connected = accounts.length > 0;
      this._accounts = accounts;
      debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum connected (via eth_requestAccounts)", {
        walletId: this.walletId,
        walletName: this.walletName,
        accountCount: accounts.length,
        accounts
      });
      return accounts;
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum connect failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  async disconnect() {
    debug.log(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum disconnect", {
      walletId: this.walletId,
      walletName: this.walletName
    });
    try {
      await this.provider.disconnect();
      this._connected = false;
      this._accounts = [];
      debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum disconnected", {
        walletId: this.walletId,
        walletName: this.walletName
      });
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum disconnect failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  async signPersonalMessage(message, address) {
    const messagePreview = message.length > 50 ? message.substring(0, 50) + "..." : message;
    debug.log(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum signPersonalMessage", {
      walletId: this.walletId,
      walletName: this.walletName,
      messagePreview,
      messageLength: message.length,
      address
    });
    try {
      const providerConnected = this.provider.isConnected?.() || this.provider.connected || false;
      if (!this._connected || this._accounts.length === 0 || !providerConnected) {
        debug.log(DebugCategory.INJECTED_PROVIDER, "Not connected, attempting to connect before signing", {
          walletId: this.walletId,
          walletName: this.walletName,
          internalConnected: this._connected,
          accountsLength: this._accounts.length,
          providerConnected
        });
        await this.connect();
      }
      const normalizedAddress = address.toLowerCase();
      const normalizedAccounts = this._accounts.map((acc) => acc.toLowerCase());
      if (!normalizedAccounts.includes(normalizedAddress)) {
        debug.warn(DebugCategory.INJECTED_PROVIDER, "Address not in connected accounts, refreshing connection", {
          walletId: this.walletId,
          walletName: this.walletName,
          requestedAddress: address,
          connectedAccounts: this._accounts
        });
        const currentAccounts = await this.getAccounts();
        const normalizedCurrentAccounts = currentAccounts.map((acc) => acc.toLowerCase());
        if (!normalizedCurrentAccounts.includes(normalizedAddress)) {
          throw new Error(`Address ${address} is not connected. Connected accounts: ${currentAccounts.join(", ")}`);
        }
        this._accounts = currentAccounts;
      }
      if (typeof this.provider.signPersonalMessage === "function") {
        const result2 = await this.provider.signPersonalMessage(message, address);
        debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum signPersonalMessage success", {
          walletId: this.walletId,
          walletName: this.walletName,
          signatureLength: result2.length
        });
        return result2;
      }
      const result = await this.request({
        method: "personal_sign",
        params: [message, address]
      });
      debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum signPersonalMessage success", {
        walletId: this.walletId,
        walletName: this.walletName,
        signatureLength: result.length
      });
      return result;
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum signPersonalMessage failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error),
        errorCode: error?.code
      });
      throw error;
    }
  }
  async signTypedData(typedData, address) {
    debug.log(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum signTypedData", {
      walletId: this.walletId,
      walletName: this.walletName,
      primaryType: typedData?.primaryType,
      address
    });
    try {
      if (typeof this.provider.signTypedData === "function") {
        const result2 = await this.provider.signTypedData(typedData, address);
        debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum signTypedData success", {
          walletId: this.walletId,
          walletName: this.walletName,
          signatureLength: result2.length
        });
        return result2;
      }
      const result = await this.request({
        method: "eth_signTypedData_v4",
        params: [address, typedData]
      });
      debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum signTypedData success", {
        walletId: this.walletId,
        walletName: this.walletName,
        signatureLength: result.length
      });
      return result;
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum signTypedData failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  async signTransaction(transaction) {
    debug.log(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum signTransaction", {
      walletId: this.walletId,
      walletName: this.walletName,
      from: transaction.from,
      to: transaction.to
    });
    try {
      if (typeof this.provider.signTransaction === "function") {
        const result2 = await this.provider.signTransaction(transaction);
        debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum signTransaction success", {
          walletId: this.walletId,
          walletName: this.walletName,
          signatureLength: result2.length
        });
        return result2;
      }
      const result = await this.request({
        method: "eth_signTransaction",
        params: [transaction]
      });
      debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum signTransaction success", {
        walletId: this.walletId,
        walletName: this.walletName,
        signatureLength: result.length
      });
      return result;
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum signTransaction failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  async sendTransaction(transaction) {
    debug.log(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum sendTransaction", {
      walletId: this.walletId,
      walletName: this.walletName,
      from: transaction.from,
      to: transaction.to,
      value: transaction.value
    });
    try {
      if (typeof this.provider.sendTransaction === "function") {
        const result2 = await this.provider.sendTransaction(transaction);
        debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum sendTransaction success", {
          walletId: this.walletId,
          walletName: this.walletName,
          txHash: result2
        });
        return result2;
      }
      const result = await this.request({
        method: "eth_sendTransaction",
        params: [transaction]
      });
      debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum sendTransaction success", {
        walletId: this.walletId,
        walletName: this.walletName,
        txHash: result
      });
      return result;
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum sendTransaction failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  async switchChain(chainId) {
    debug.log(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum switchChain", {
      walletId: this.walletId,
      walletName: this.walletName,
      chainId
    });
    try {
      const hexChainId = typeof chainId === "string" ? chainId.toLowerCase().startsWith("0x") ? chainId : `0x${parseInt(chainId, 10).toString(16)}` : `0x${chainId.toString(16)}`;
      if (typeof this.provider.switchChain === "function") {
        await this.provider.switchChain(hexChainId);
      } else {
        await this.request({ method: "wallet_switchEthereumChain", params: [{ chainId: hexChainId }] });
      }
      this._chainId = hexChainId;
      this.eventEmitter.emit("chainChanged", this._chainId);
      debug.info(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum switchChain success", {
        walletId: this.walletId,
        walletName: this.walletName,
        chainId: hexChainId
      });
    } catch (error) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "External wallet Ethereum switchChain failed", {
        walletId: this.walletId,
        walletName: this.walletName,
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }
  async getChainId() {
    if (typeof this.provider.getChainId === "function") {
      const chainId2 = await this.provider.getChainId();
      this._chainId = `0x${chainId2.toString(16)}`;
      return chainId2;
    }
    const chainId = await this.request({ method: "eth_chainId" });
    const parsed = parseInt(chainId, 16);
    this._chainId = chainId;
    return parsed;
  }
  async getAccounts() {
    if (typeof this.provider.getAccounts === "function") {
      const accounts2 = await this.provider.getAccounts();
      this._accounts = accounts2;
      return accounts2;
    }
    const accounts = await this.request({ method: "eth_accounts" });
    this._accounts = accounts;
    return accounts;
  }
  isConnected() {
    return this._connected;
  }
  setupEventListeners() {
    if (typeof this.provider.on === "function") {
      this.provider.on("connect", (info) => {
        this._connected = true;
        this._chainId = info.chainId;
        this.eventEmitter.emit("connect", info);
      });
      this.provider.on("disconnect", (error) => {
        this._connected = false;
        this._accounts = [];
        this.eventEmitter.emit("disconnect", error);
        this.eventEmitter.emit("accountsChanged", []);
      });
      this.provider.on("accountsChanged", (accounts) => {
        this._accounts = accounts;
        this._connected = accounts.length > 0;
        this.eventEmitter.emit("accountsChanged", accounts);
      });
      this.provider.on("chainChanged", (chainId) => {
        this._chainId = chainId;
        this.eventEmitter.emit("chainChanged", chainId);
      });
    }
  }
  on(event, listener) {
    this.eventEmitter.on(event, listener);
  }
  off(event, listener) {
    this.eventEmitter.off(event, listener);
  }
};

// src/wallets/registry.ts
function isPhantomWallet(wallet) {
  return wallet !== void 0 && wallet.id === "phantom" && "isPhantom" in wallet && wallet.isPhantom === true;
}
function isWalletStandardWallet(provider) {
  return provider !== null && typeof provider === "object" && "features" in provider && typeof provider.features === "object";
}
var InjectedWalletRegistry = class {
  constructor() {
    this.wallets = /* @__PURE__ */ new Map();
    this.discoveryPromise = null;
  }
  register(info) {
    const wrappedProviders = {};
    if (info.providers?.solana) {
      if (isWalletStandardWallet(info.providers.solana)) {
        wrappedProviders.solana = new WalletStandardSolanaAdapter(info.providers.solana, info.id, info.name);
        debug.log(DebugCategory.BROWSER_SDK, "Wrapped Wallet Standard Solana wallet with adapter", {
          walletId: info.id,
          walletName: info.name
        });
      } else {
        wrappedProviders.solana = new InjectedWalletSolanaChain(info.providers.solana, info.id, info.name);
        debug.log(DebugCategory.BROWSER_SDK, "Wrapped Solana provider with InjectedWalletSolanaChain", {
          walletId: info.id,
          walletName: info.name
        });
      }
    }
    if (info.providers?.ethereum) {
      wrappedProviders.ethereum = new InjectedWalletEthereumChain(info.providers.ethereum, info.id, info.name);
      debug.log(DebugCategory.BROWSER_SDK, "Wrapped Ethereum provider with InjectedWalletEthereumChain", {
        walletId: info.id,
        walletName: info.name
      });
    }
    const wrappedInfo = {
      ...info,
      providers: Object.keys(wrappedProviders).length > 0 ? wrappedProviders : info.providers
    };
    this.wallets.set(info.id, wrappedInfo);
  }
  /**
   * Register Phantom wallet with its instance
   * This creates wrapped providers and stores the Phantom instance for auto-confirm access
   * Uses unified InjectedWallet chains for both Phantom and external wallets
   */
  registerPhantom(phantomInstance, addressTypes) {
    const wrappedProviders = {};
    if (addressTypes.includes(AddressType.solana) && phantomInstance.solana) {
      wrappedProviders.solana = new InjectedWalletSolanaChain(phantomInstance.solana, "phantom", "Phantom");
      debug.log(DebugCategory.BROWSER_SDK, "Created InjectedWalletSolanaChain wrapper for Phantom", {
        walletId: "phantom"
      });
    }
    if (addressTypes.includes(AddressType.ethereum) && phantomInstance.ethereum) {
      wrappedProviders.ethereum = new InjectedWalletEthereumChain(phantomInstance.ethereum, "phantom", "Phantom");
      debug.log(DebugCategory.BROWSER_SDK, "Created InjectedWalletEthereumChain wrapper for Phantom", {
        walletId: "phantom"
      });
    }
    const phantomWallet = {
      id: "phantom",
      name: "Phantom",
      icon: "",
      // Icon will be rendered from icons package in UI components
      addressTypes,
      providers: wrappedProviders,
      isPhantom: true,
      phantomInstance,
      discovery: "phantom"
    };
    this.wallets.set("phantom", phantomWallet);
    debug.log(DebugCategory.BROWSER_SDK, "Registered Phantom wallet with chain wrappers", {
      addressTypes,
      hasSolana: !!wrappedProviders.solana,
      hasEthereum: !!wrappedProviders.ethereum
    });
  }
  unregister(id) {
    this.wallets.delete(id);
  }
  has(id) {
    return this.wallets.has(id);
  }
  getById(id) {
    return this.wallets.get(id);
  }
  getAll() {
    return Array.from(this.wallets.values());
  }
  getByAddressTypes(addressTypes) {
    if (addressTypes.length === 0) {
      return this.getAll();
    }
    const allowed = new Set(addressTypes);
    return this.getAll().filter((wallet) => wallet.addressTypes.some((t) => allowed.has(t)));
  }
  discover(addressTypes) {
    if (this.discoveryPromise) {
      return this.discoveryPromise;
    }
    debug.log(DebugCategory.BROWSER_SDK, "Starting wallet discovery", { addressTypes });
    this.discoveryPromise = discoverWallets(addressTypes).then((discoveredWallets) => {
      const relevantWallets = addressTypes ? discoveredWallets.filter((wallet) => wallet.addressTypes.some((type) => addressTypes.includes(type))) : discoveredWallets;
      for (const wallet of relevantWallets) {
        if (wallet.id === "phantom" && isPhantomWallet(wallet)) {
          this.registerPhantom(wallet.phantomInstance, wallet.addressTypes);
        } else {
          this.register(wallet);
        }
        debug.log(DebugCategory.BROWSER_SDK, "Registered discovered wallet", {
          id: wallet.id,
          name: wallet.name,
          addressTypes: wallet.addressTypes
        });
      }
      debug.info(DebugCategory.BROWSER_SDK, "Wallet discovery completed", {
        totalDiscovered: discoveredWallets.length,
        relevantWallets: relevantWallets.length
      });
    }).catch((error) => {
      debug.warn(DebugCategory.BROWSER_SDK, "Wallet discovery failed", { error });
      this.discoveryPromise = null;
      throw error;
    });
    return this.discoveryPromise;
  }
};
var walletRegistry = null;
function getWalletRegistry() {
  if (!walletRegistry) {
    walletRegistry = new InjectedWalletRegistry();
  }
  return walletRegistry;
}

// src/providers/injected/index.ts
var WAS_CONNECTED_KEY = "phantom-injected-was-connected";
var WAS_CONNECTED_VALUE = "true";
var LAST_WALLET_ID_KEY = "phantom-injected-last-wallet-id";
var InjectedProvider = class {
  // Store cleanups per walletId
  constructor(config) {
    this.selectedWalletId = null;
    this.walletStates = /* @__PURE__ */ new Map();
    // Event management
    this.eventListeners = /* @__PURE__ */ new Map();
    this.eventsInitialized = false;
    this.eventListenersSetup = /* @__PURE__ */ new Set();
    // Track walletId that have listeners set up
    this.eventListenerCleanups = /* @__PURE__ */ new Map();
    debug.log(DebugCategory.INJECTED_PROVIDER, "Initializing InjectedProvider", { config });
    this.addressTypes = config.addressTypes;
    this.walletRegistry = getWalletRegistry();
    debug.log(DebugCategory.INJECTED_PROVIDER, "Address types configured", { addressTypes: this.addressTypes });
    this.walletRegistry.discover(this.addressTypes).catch((error) => {
      debug.warn(DebugCategory.INJECTED_PROVIDER, "Wallet discovery failed during initialization", { error });
    });
    debug.info(DebugCategory.INJECTED_PROVIDER, "InjectedProvider initialized");
  }
  /**
   * Wait for wallet discovery to complete if the wallet is not yet in the registry
   * This is needed for auto-connect when the last wallet was an external wallet
   */
  async waitForWalletDiscovery(walletId) {
    if (this.walletRegistry.has(walletId)) {
      return;
    }
    debug.log(DebugCategory.INJECTED_PROVIDER, "Wallet not found in registry, waiting for discovery", {
      walletId
    });
    try {
      await this.walletRegistry.discover(this.addressTypes);
      debug.log(DebugCategory.INJECTED_PROVIDER, "Wallet discovery completed", { walletId });
    } catch (error) {
      debug.warn(DebugCategory.INJECTED_PROVIDER, "Wallet discovery failed", {
        walletId,
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }
  /**
   * Helper method to get a chain provider with consistent error handling
   */
  getChainProvider(addressType, providerKey, chainName) {
    if (!this.addressTypes.includes(addressType)) {
      throw new Error(`${chainName} not enabled for this provider`);
    }
    const walletId = this.selectedWalletId || "phantom";
    const walletInfo = this.walletRegistry.getById(walletId);
    if (!walletInfo) {
      const registry = this.walletRegistry;
      if (registry.discoveryPromise) {
        throw new Error(
          `Wallet "${walletId}" not found. Wallet discovery is still in progress. Please wait for sdk.discoverWallets() to complete before accessing chain properties.`
        );
      }
      throw new Error(
        `Wallet "${walletId}" not found. Please ensure wallet discovery has completed. Make sure you call sdk.discoverWallets() and await it before accessing chain properties.`
      );
    }
    const provider = walletInfo.providers?.[providerKey];
    if (!provider) {
      throw new Error(
        `Selected wallet "${walletInfo.name}" does not support ${chainName}. This wallet only supports: ${walletInfo.addressTypes.join(", ")}. Make sure your SDK config includes ${chainName} in addressTypes.`
      );
    }
    return provider;
  }
  get solana() {
    return this.getChainProvider(AddressType2.solana, "solana", "Solana");
  }
  /**
   * Access to Ethereum chain operations
   */
  get ethereum() {
    return this.getChainProvider(AddressType2.ethereum, "ethereum", "Ethereum");
  }
  validateAndSelectWallet(requestedWalletId) {
    if (!this.walletRegistry.has(requestedWalletId)) {
      debug.error(DebugCategory.INJECTED_PROVIDER, "Unknown injected wallet id requested", {
        walletId: requestedWalletId
      });
      throw new Error(`Unknown injected wallet id: ${requestedWalletId}`);
    }
    const walletInfo = this.walletRegistry.getById(requestedWalletId);
    if (!walletInfo || !walletInfo.providers) {
      debug.warn(DebugCategory.INJECTED_PROVIDER, "Wallet not available for connection", {
        walletId: requestedWalletId
      });
      throw new Error(`Wallet not available for connection: ${requestedWalletId}`);
    }
    this.selectedWalletId = requestedWalletId;
    debug.log(DebugCategory.INJECTED_PROVIDER, "Selected injected wallet for connection", {
      walletId: requestedWalletId
    });
    return walletInfo;
  }
  async connectToWallet(walletInfo, options) {
    if (!walletInfo.providers) {
      const error = new Error(`Wallet adapter not available for wallet: ${this.selectedWalletId}`);
      debug.error(DebugCategory.INJECTED_PROVIDER, "Wallet adapter not available", { walletId: this.selectedWalletId });
      this.emit("connect_error", {
        error: error.message,
        source: options?.skipEventListeners ? "auto-connect" : "manual-connect"
      });
      throw error;
    }
    debug.log(DebugCategory.INJECTED_PROVIDER, "Connecting via wallet", {
      walletId: this.selectedWalletId,
      walletName: walletInfo.name,
      options
    });
    if (!options?.skipEventListeners) {
      this.setupEventListeners(walletInfo);
    }
    const connectedAddresses = [];
    if (this.addressTypes.includes(AddressType2.solana) && walletInfo.providers?.solana) {
      debug.log(DebugCategory.INJECTED_PROVIDER, "Attempting Solana connection", {
        walletId: this.selectedWalletId,
        walletName: walletInfo.name,
        onlyIfTrusted: options?.onlyIfTrusted
      });
      try {
        const result = await walletInfo.providers.solana.connect(
          options?.onlyIfTrusted ? { onlyIfTrusted: true } : void 0
        );
        const address = result.publicKey;
        connectedAddresses.push({
          addressType: AddressType2.solana,
          address
        });
        debug.info(DebugCategory.INJECTED_PROVIDER, "Solana connected successfully", {
          address,
          walletId: this.selectedWalletId,
          walletName: walletInfo.name
        });
      } catch (err) {
        debug.warn(DebugCategory.INJECTED_PROVIDER, "Failed to connect Solana, stopping", {
          error: err,
          walletId: this.selectedWalletId,
          walletName: walletInfo.name
        });
        this.emit("connect_error", {
          error: err instanceof Error ? err.message : "Failed to connect",
          source: options?.skipEventListeners ? "auto-connect" : "manual-connect"
        });
        throw err;
      }
    }
    if (this.addressTypes.includes(AddressType2.ethereum) && walletInfo.providers?.ethereum) {
      debug.log(DebugCategory.INJECTED_PROVIDER, "Attempting Ethereum connection", {
        walletId: this.selectedWalletId,
        walletName: walletInfo.name,
        silent: options?.silent
      });
      try {
        let accounts;
        if (options?.silent) {
          accounts = await walletInfo.providers.ethereum.request({ method: "eth_accounts" });
        } else {
          accounts = await walletInfo.providers.ethereum.connect();
        }
        if (accounts.length > 0) {
          connectedAddresses.push(
            ...accounts.map((address) => ({
              addressType: AddressType2.ethereum,
              address
            }))
          );
          debug.info(DebugCategory.INJECTED_PROVIDER, "Ethereum connected successfully", {
            addresses: accounts,
            walletId: this.selectedWalletId,
            walletName: walletInfo.name
          });
        }
      } catch (err) {
        debug.warn(DebugCategory.INJECTED_PROVIDER, "Failed to connect Ethereum, stopping", {
          error: err,
          walletId: this.selectedWalletId,
          walletName: walletInfo.name
        });
        this.emit("connect_error", {
          error: err instanceof Error ? err.message : "Failed to connect",
          source: options?.skipEventListeners ? "auto-connect" : "manual-connect"
        });
        throw err;
      }
    }
    return connectedAddresses;
  }
  async finalizeConnection(connectedAddresses, authProvider, walletId) {
    if (connectedAddresses.length === 0) {
      const error = new Error("Failed to connect to any supported wallet provider");
      this.emit("connect_error", {
        error: error.message,
        source: "manual-connect"
      });
      throw error;
    }
    if (this.selectedWalletId) {
      this.setWalletState(this.selectedWalletId, {
        connected: true,
        addresses: connectedAddresses
      });
    }
    debug.log(DebugCategory.INJECTED_PROVIDER, "Finalized connection with addresses", {
      addressCount: connectedAddresses.length,
      addresses: connectedAddresses.map((addr) => ({
        type: addr.addressType,
        address: addr.address.substring(0, 10) + "..."
      }))
    });
    const authUserId = await this.getAuthUserId("manual-connect");
    try {
      localStorage.setItem(WAS_CONNECTED_KEY, WAS_CONNECTED_VALUE);
      debug.log(DebugCategory.INJECTED_PROVIDER, "Set was-connected flag - auto-reconnect enabled");
      if (this.selectedWalletId) {
        localStorage.setItem(LAST_WALLET_ID_KEY, this.selectedWalletId);
        debug.log(DebugCategory.INJECTED_PROVIDER, "Stored last injected wallet id", {
          walletId: this.selectedWalletId
        });
      }
    } catch (error) {
      debug.warn(DebugCategory.INJECTED_PROVIDER, "Failed to persist injected provider state", { error });
    }
    const walletInfo = walletId ? this.walletRegistry.getById(walletId) : void 0;
    const wallet = walletInfo ? {
      id: walletInfo.id,
      name: walletInfo.name,
      icon: walletInfo.icon,
      addressTypes: walletInfo.addressTypes,
      rdns: walletInfo.rdns,
      discovery: walletInfo.discovery
    } : void 0;
    const result = {
      addresses: connectedAddresses,
      status: "completed",
      authUserId,
      authProvider,
      walletId,
      wallet
    };
    this.emit("connect", {
      addresses: connectedAddresses,
      source: "manual-connect",
      authUserId
    });
    return result;
  }
  async connect(authOptions) {
    debug.info(DebugCategory.INJECTED_PROVIDER, "Starting injected provider connect", {
      addressTypes: this.addressTypes,
      provider: authOptions.provider
    });
    if (authOptions.provider !== "injected") {
      throw new Error(`Invalid provider for injected connection: ${authOptions.provider}. Must be "injected"`);
    }
    this.emit("connect_start", {
      source: "manual-connect",
      providerType: "injected"
    });
    try {
      const requestedWalletId = authOptions.walletId || "phantom";
      const walletInfo = this.validateAndSelectWallet(requestedWalletId);
      const connectedAddresses = await this.connectToWallet(walletInfo);
      return await this.finalizeConnection(connectedAddresses, "injected", this.selectedWalletId || void 0);
    } catch (error) {
      this.emit("connect_error", {
        error: error instanceof Error ? error.message : "Failed to connect",
        source: "manual-connect"
      });
      throw error;
    }
  }
  async disconnect() {
    debug.info(DebugCategory.INJECTED_PROVIDER, "Starting injected provider disconnect");
    const walletInfo = this.walletRegistry.getById(this.selectedWalletId || "phantom");
    if (walletInfo?.providers) {
      if (this.addressTypes.includes(AddressType2.solana) && walletInfo.providers.solana) {
        try {
          await walletInfo.providers.solana.disconnect();
          debug.log(DebugCategory.INJECTED_PROVIDER, "Solana disconnected successfully");
        } catch (err) {
          debug.warn(DebugCategory.INJECTED_PROVIDER, "Failed to disconnect Solana", { error: err });
        }
      }
      if (this.addressTypes.includes(AddressType2.ethereum) && walletInfo.providers.ethereum) {
        try {
          await walletInfo.providers.ethereum.disconnect();
          debug.log(DebugCategory.INJECTED_PROVIDER, "Ethereum disconnected successfully");
        } catch (err) {
          debug.warn(DebugCategory.INJECTED_PROVIDER, "Failed to disconnect Ethereum", { error: err });
        }
      }
    }
    const walletId = this.selectedWalletId || "phantom";
    const cleanups = this.eventListenerCleanups.get(walletId);
    if (cleanups) {
      cleanups.forEach((cleanup) => cleanup());
      this.eventListenerCleanups.delete(walletId);
    }
    this.eventListenersSetup.delete(walletId);
    if (this.selectedWalletId) {
      this.setWalletState(this.selectedWalletId, {
        connected: false,
        addresses: []
      });
    }
    try {
      localStorage.removeItem(WAS_CONNECTED_KEY);
      debug.log(DebugCategory.INJECTED_PROVIDER, "Cleared was connected flag to prevent auto-reconnect");
    } catch (error) {
      debug.warn(DebugCategory.INJECTED_PROVIDER, "Failed to clear was-connected flag", { error });
    }
    this.emit("disconnect", {
      source: "manual-disconnect"
    });
    debug.info(DebugCategory.INJECTED_PROVIDER, "Injected provider disconnected successfully");
  }
  /**
   * Attempt auto-connection if user was previously connected
   * Only reconnects if the user connected before and didn't explicitly disconnect
   * Should be called after setting up event listeners
   */
  async autoConnect() {
    debug.log(DebugCategory.INJECTED_PROVIDER, "Attempting auto-connect");
    let lastWalletId = null;
    try {
      const wasConnected = localStorage.getItem(WAS_CONNECTED_KEY);
      if (wasConnected !== WAS_CONNECTED_VALUE) {
        debug.log(DebugCategory.INJECTED_PROVIDER, "Skipping auto-connect: user was not previously connected");
        return;
      }
      lastWalletId = localStorage.getItem(LAST_WALLET_ID_KEY);
      debug.log(DebugCategory.INJECTED_PROVIDER, "User was previously connected, attempting auto-connect", {
        lastWalletId: lastWalletId || "phantom"
      });
    } catch (error) {
      debug.warn(DebugCategory.INJECTED_PROVIDER, "Failed to check was-connected flag", { error });
      return;
    }
    this.emit("connect_start", {
      source: "auto-connect",
      providerType: "injected"
    });
    try {
      const walletId = lastWalletId || "phantom";
      await this.waitForWalletDiscovery(walletId);
      const walletInfo = this.validateAndSelectWallet(walletId);
      let connectedAddresses = [];
      try {
        connectedAddresses = await this.connectToWallet(walletInfo, {
          onlyIfTrusted: true,
          silent: true,
          skipEventListeners: true
          // Set up listeners only if connection succeeds
        });
      } catch (err) {
        debug.log(DebugCategory.INJECTED_PROVIDER, "Auto-connect failed (expected if not trusted)", {
          error: err,
          walletId: this.selectedWalletId
        });
      }
      if (connectedAddresses.length === 0) {
        debug.log(DebugCategory.INJECTED_PROVIDER, "Auto-connect failed: no trusted connections available");
        this.emit("connect_error", {
          error: "No trusted connections available",
          source: "auto-connect"
        });
        return;
      }
      this.setupEventListeners(walletInfo);
      if (this.selectedWalletId) {
        this.setWalletState(this.selectedWalletId, {
          connected: true,
          addresses: connectedAddresses
        });
      }
      const authUserId = await this.getAuthUserId("auto-connect");
      this.emit("connect", {
        addresses: connectedAddresses,
        source: "auto-connect",
        authUserId
      });
      debug.info(DebugCategory.INJECTED_PROVIDER, "Auto-connect successful", {
        addressCount: connectedAddresses.length,
        addresses: connectedAddresses.map((addr) => ({
          type: addr.addressType,
          address: addr.address.substring(0, 8) + "..."
        })),
        walletId: this.selectedWalletId,
        authUserId
      });
    } catch (error) {
      debug.log(DebugCategory.INJECTED_PROVIDER, "Auto-connect failed with error", {
        error: error instanceof Error ? error.message : String(error)
      });
      this.emit("connect_error", {
        error: error instanceof Error ? error.message : "Auto-connect failed",
        source: "auto-connect"
      });
    }
  }
  getWalletState(walletId) {
    if (!this.walletStates.has(walletId)) {
      this.walletStates.set(walletId, { connected: false, addresses: [] });
    }
    return this.walletStates.get(walletId);
  }
  setWalletState(walletId, state) {
    this.walletStates.set(walletId, state);
  }
  /**
   * Update wallet state with new addresses for a specific address type
   * Replaces all existing addresses of the given type with the new addresses
   * @param walletId - The wallet ID to update
   * @param newAddresses - Array of new addresses (strings) for the address type
   * @param addressType - The type of addresses being updated
   * @returns The updated addresses array
   */
  updateWalletAddresses(walletId, newAddresses, addressType) {
    const state = this.getWalletState(walletId);
    const otherAddresses = state.addresses.filter((addr) => addr.addressType !== addressType);
    const addressesOfType = newAddresses.map((address) => ({ addressType, address }));
    const updatedAddresses = [...otherAddresses, ...addressesOfType];
    this.setWalletState(walletId, {
      connected: updatedAddresses.length > 0,
      addresses: updatedAddresses
    });
    return updatedAddresses;
  }
  /**
   * Helper to construct account change source string
   */
  getAccountChangeSource(source) {
    return `${source}-account-change`;
  }
  /**
   * Create a handler for Solana connect events
   */
  createSolanaConnectHandler(walletId, source) {
    return async (publicKey) => {
      debug.log(DebugCategory.INJECTED_PROVIDER, "Solana connect event received", { publicKey, walletId });
      const newAddresses = this.updateWalletAddresses(walletId, [publicKey], AddressType2.solana);
      const authUserId = await this.getAuthUserId("Solana connect event");
      this.emit("connect", {
        addresses: newAddresses,
        source,
        authUserId
      });
    };
  }
  /**
   * Create a handler for Solana disconnect events
   */
  createSolanaDisconnectHandler(walletId, source) {
    return () => {
      debug.log(DebugCategory.INJECTED_PROVIDER, "Solana disconnect event received", { walletId });
      const state = this.getWalletState(walletId);
      const filteredAddresses = state.addresses.filter((addr) => addr.addressType !== AddressType2.solana);
      this.setWalletState(walletId, {
        connected: filteredAddresses.length > 0,
        addresses: filteredAddresses
      });
      this.emit("disconnect", {
        source
      });
    };
  }
  /**
   * Create a handler for Solana account change events
   * Can receive string | null per Wallet Standard
   */
  createSolanaAccountChangeHandler(walletId, source) {
    return async (publicKey) => {
      debug.log(DebugCategory.INJECTED_PROVIDER, "Solana account changed event received", { publicKey, walletId });
      if (publicKey) {
        const newAddresses = this.updateWalletAddresses(walletId, [publicKey], AddressType2.solana);
        const authUserId = await this.getAuthUserId("Solana account changed event");
        this.emit("connect", {
          addresses: newAddresses,
          source: this.getAccountChangeSource(source),
          authUserId
        });
      } else {
        const state = this.getWalletState(walletId);
        const otherAddresses = state.addresses.filter((addr) => addr.addressType !== AddressType2.solana);
        this.setWalletState(walletId, {
          connected: otherAddresses.length > 0,
          addresses: otherAddresses
        });
        this.emit("disconnect", {
          source: this.getAccountChangeSource(source)
        });
      }
    };
  }
  /**
   * Create a handler for Ethereum connect events
   * EIP-1193 connect event receives { chainId: string }, but we need to get accounts separately
   */
  createEthereumConnectHandler(walletId, source) {
    return async (connectInfo) => {
      let accounts = [];
      if (Array.isArray(connectInfo)) {
        accounts = connectInfo;
      } else {
        try {
          const walletInfo = this.walletRegistry.getById(walletId);
          if (walletInfo?.providers?.ethereum) {
            accounts = await walletInfo.providers.ethereum.getAccounts();
          }
        } catch (error) {
          debug.warn(DebugCategory.INJECTED_PROVIDER, "Failed to get accounts on connect", { error });
        }
      }
      debug.log(DebugCategory.INJECTED_PROVIDER, "Ethereum connect event received", { accounts, walletId });
      if (accounts.length > 0) {
        const newAddresses = this.updateWalletAddresses(walletId, accounts, AddressType2.ethereum);
        const authUserId = await this.getAuthUserId("Ethereum connect event");
        this.emit("connect", {
          addresses: newAddresses,
          source,
          authUserId
        });
      }
    };
  }
  /**
   * Create a handler for Ethereum disconnect events
   */
  createEthereumDisconnectHandler(walletId, source) {
    return () => {
      debug.log(DebugCategory.INJECTED_PROVIDER, "Ethereum disconnect event received", { walletId });
      const state = this.getWalletState(walletId);
      const filteredAddresses = state.addresses.filter((addr) => addr.addressType !== AddressType2.ethereum);
      this.setWalletState(walletId, {
        connected: filteredAddresses.length > 0,
        addresses: filteredAddresses
      });
      this.emit("disconnect", {
        source
      });
    };
  }
  /**
   * Create a handler for Ethereum account change events
   */
  createEthereumAccountChangeHandler(walletId, source) {
    return async (accounts) => {
      debug.log(DebugCategory.INJECTED_PROVIDER, "Ethereum accounts changed event received", { accounts, walletId });
      if (accounts && accounts.length > 0) {
        const newAddresses = this.updateWalletAddresses(walletId, accounts, AddressType2.ethereum);
        const authUserId = await this.getAuthUserId("Ethereum accounts changed event");
        this.emit("connect", {
          addresses: newAddresses,
          source: this.getAccountChangeSource(source),
          authUserId
        });
      } else {
        const state = this.getWalletState(walletId);
        const otherAddresses = state.addresses.filter((addr) => addr.addressType !== AddressType2.ethereum);
        this.setWalletState(walletId, {
          connected: otherAddresses.length > 0,
          addresses: otherAddresses
        });
        this.emit("disconnect", {
          source: this.getAccountChangeSource(source)
        });
      }
    };
  }
  getAddresses() {
    const walletId = this.selectedWalletId || "phantom";
    return this.getWalletState(walletId).addresses;
  }
  /**
   * Get enabled address types for the current selected wallet
   * - For Phantom: returns config.addressTypes
   * - For external wallets: returns the wallet's addressTypes from registry
   */
  getEnabledAddressTypes() {
    if (!this.selectedWalletId || this.selectedWalletId === "phantom") {
      return this.addressTypes;
    }
    const walletInfo = this.walletRegistry.getById(this.selectedWalletId);
    if (walletInfo) {
      return walletInfo.addressTypes;
    }
    return this.addressTypes;
  }
  isConnected() {
    const walletId = this.selectedWalletId || "phantom";
    return this.getWalletState(walletId).connected;
  }
  // AutoConfirm methods - only available for Phantom wallet
  async enableAutoConfirm(params) {
    const walletInfo = this.walletRegistry.getById(this.selectedWalletId || "phantom");
    if (!isPhantomWallet(walletInfo)) {
      throw new Error("Auto-confirm is only available for Phantom wallet");
    }
    debug.log(DebugCategory.INJECTED_PROVIDER, "Enabling autoConfirm", { params });
    return await walletInfo.phantomInstance.autoConfirm.autoConfirmEnable(params);
  }
  async disableAutoConfirm() {
    const walletInfo = this.walletRegistry.getById(this.selectedWalletId || "phantom");
    if (!isPhantomWallet(walletInfo)) {
      throw new Error("Auto-confirm is only available for Phantom wallet");
    }
    debug.log(DebugCategory.INJECTED_PROVIDER, "Disabling autoConfirm");
    await walletInfo.phantomInstance.autoConfirm.autoConfirmDisable();
  }
  async getAutoConfirmStatus() {
    const walletInfo = this.walletRegistry.getById(this.selectedWalletId || "phantom");
    if (!isPhantomWallet(walletInfo)) {
      throw new Error("Auto-confirm is only available for Phantom wallet");
    }
    debug.log(DebugCategory.INJECTED_PROVIDER, "Getting autoConfirm status");
    return await walletInfo.phantomInstance.autoConfirm.autoConfirmStatus();
  }
  async getSupportedAutoConfirmChains() {
    const walletInfo = this.walletRegistry.getById(this.selectedWalletId || "phantom");
    if (!isPhantomWallet(walletInfo)) {
      throw new Error("Auto-confirm is only available for Phantom wallet");
    }
    debug.log(DebugCategory.INJECTED_PROVIDER, "Getting supported autoConfirm chains");
    return await walletInfo.phantomInstance.autoConfirm.autoConfirmSupportedChains();
  }
  /**
   * Helper method to get authUserId from window.phantom.app.getUser()
   * Returns undefined if the method is not available or fails, or if wallet is not Phantom
   */
  async getAuthUserId(context) {
    const walletInfo = this.walletRegistry.getById(this.selectedWalletId || "phantom");
    if (!isPhantomWallet(walletInfo)) {
      return void 0;
    }
    try {
      if (window.phantom?.app?.getUser) {
        const userInfo = await window.phantom.app.getUser();
        const authUserId = userInfo?.authUserId;
        if (authUserId) {
          debug.log(
            DebugCategory.INJECTED_PROVIDER,
            `Retrieved authUserId from window.phantom.app.getUser() during ${context}`,
            {
              authUserId
            }
          );
        }
        return authUserId;
      }
    } catch (error) {
      debug.log(
        DebugCategory.INJECTED_PROVIDER,
        `Failed to get user info during ${context} (method may not be supported)`,
        { error }
      );
    }
    return void 0;
  }
  // Event management methods - implementing unified event interface
  on(event, callback) {
    debug.log(DebugCategory.INJECTED_PROVIDER, "Adding event listener", { event });
    if (!this.eventsInitialized) {
      const walletId = this.selectedWalletId || "phantom";
      const walletInfo = this.walletRegistry.getById(walletId);
      if (walletInfo) {
        this.setupEventListeners(walletInfo);
      }
    }
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, /* @__PURE__ */ new Set());
    }
    this.eventListeners.get(event)?.add(callback);
  }
  off(event, callback) {
    debug.log(DebugCategory.INJECTED_PROVIDER, "Removing event listener", { event });
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event)?.delete(callback);
      if (this.eventListeners.get(event)?.size === 0) {
        this.eventListeners.delete(event);
      }
    }
  }
  emit(event, data) {
    debug.log(DebugCategory.INJECTED_PROVIDER, "Emitting event", {
      event,
      listenerCount: this.eventListeners.get(event)?.size || 0,
      data
    });
    const listeners = this.eventListeners.get(event);
    if (listeners && listeners.size > 0) {
      listeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          debug.error(DebugCategory.INJECTED_PROVIDER, "Event callback error", { event, error });
        }
      });
    }
  }
  /**
   * Set up Solana event listeners for any provider (Phantom or external)
   */
  setupSolanaEventListeners(provider, walletId, source) {
    if (typeof provider.on !== "function")
      return;
    debug.log(DebugCategory.INJECTED_PROVIDER, "Setting up Solana event listeners", { walletId, source });
    const handlers = {
      connect: this.createSolanaConnectHandler(walletId, source),
      disconnect: this.createSolanaDisconnectHandler(walletId, source),
      accountChanged: this.createSolanaAccountChangeHandler(walletId, source)
    };
    provider.on("connect", handlers.connect);
    provider.on("disconnect", handlers.disconnect);
    provider.on("accountChanged", handlers.accountChanged);
    const cleanups = [];
    if (typeof provider.off === "function") {
      cleanups.push(
        () => provider.off("connect", handlers.connect),
        () => provider.off("disconnect", handlers.disconnect),
        () => provider.off("accountChanged", handlers.accountChanged)
      );
    }
    const existingCleanups = this.eventListenerCleanups.get(walletId) || [];
    this.eventListenerCleanups.set(walletId, [...existingCleanups, ...cleanups]);
  }
  /**
   * Set up Ethereum event listeners for any provider (Phantom or external)
   */
  setupEthereumEventListeners(provider, walletId, source) {
    if (typeof provider.on !== "function")
      return;
    debug.log(DebugCategory.INJECTED_PROVIDER, "Setting up Ethereum event listeners", { walletId, source });
    const handlers = {
      connect: this.createEthereumConnectHandler(walletId, source),
      disconnect: this.createEthereumDisconnectHandler(walletId, source),
      accountsChanged: this.createEthereumAccountChangeHandler(walletId, source)
    };
    provider.on("connect", handlers.connect);
    provider.on("disconnect", handlers.disconnect);
    provider.on("accountsChanged", handlers.accountsChanged);
    const cleanups = [];
    if (typeof provider.off === "function") {
      cleanups.push(
        () => provider.off("connect", handlers.connect),
        () => provider.off("disconnect", handlers.disconnect),
        () => provider.off("accountsChanged", handlers.accountsChanged)
      );
    }
    const existingCleanups = this.eventListenerCleanups.get(walletId) || [];
    this.eventListenerCleanups.set(walletId, [...existingCleanups, ...cleanups]);
  }
  /**
   * Unified event listener setup for all wallet types (Phantom and external)
   */
  setupEventListeners(walletInfo) {
    const walletId = this.selectedWalletId || "phantom";
    if (this.eventListenersSetup.has(walletId)) {
      debug.log(DebugCategory.INJECTED_PROVIDER, "Event listeners already set up for wallet", { walletId });
      return;
    }
    debug.log(DebugCategory.INJECTED_PROVIDER, "Setting up event listeners", { walletId });
    if (this.addressTypes.includes(AddressType2.solana) && walletInfo.providers?.solana) {
      this.setupSolanaEventListeners(walletInfo.providers.solana, walletId, "wallet");
    }
    if (this.addressTypes.includes(AddressType2.ethereum) && walletInfo.providers?.ethereum) {
      this.setupEthereumEventListeners(walletInfo.providers.ethereum, walletId, "wallet");
    }
    this.eventListenersSetup.add(walletId);
    this.eventsInitialized = true;
  }
};

// src/providers/embedded/index.ts
import { EmbeddedProvider as CoreEmbeddedProvider } from "@phantom/embedded-provider-core";
import { IndexedDbStamper } from "@phantom/indexed-db-stamper";

// src/providers/embedded/adapters/storage.ts
var BrowserStorage = class {
  constructor() {
    this.dbName = "phantom-browser-sdk";
    this.storeName = "sessions";
    this.version = 1;
  }
  async getDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };
    });
  }
  async getSession() {
    debug.log(DebugCategory.STORAGE, "Getting session from IndexedDB");
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);
      const request = store.get("currentSession");
      request.onsuccess = () => {
        const session = request.result || null;
        debug.log(DebugCategory.STORAGE, "Retrieved session from IndexedDB", {
          hasSession: !!session,
          sessionId: session?.sessionId
        });
        resolve(session);
      };
      request.onerror = () => {
        debug.error(DebugCategory.STORAGE, "Failed to get session from IndexedDB", { error: request.error });
        reject(request.error);
      };
    });
  }
  async saveSession(session) {
    debug.log(DebugCategory.STORAGE, "Saving session to IndexedDB", {
      sessionId: session.sessionId,
      walletId: session.walletId,
      status: session.status
    });
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.put(session, "currentSession");
      request.onsuccess = () => {
        debug.log(DebugCategory.STORAGE, "Successfully saved session to IndexedDB");
        resolve();
      };
      request.onerror = () => {
        debug.error(DebugCategory.STORAGE, "Failed to save session to IndexedDB", { error: request.error });
        reject(request.error);
      };
    });
  }
  async clearSession() {
    debug.log(DebugCategory.STORAGE, "Clearing session from IndexedDB");
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.delete("currentSession");
      request.onsuccess = () => {
        debug.log(DebugCategory.STORAGE, "Successfully cleared session from IndexedDB");
        resolve();
      };
      request.onerror = () => {
        debug.error(DebugCategory.STORAGE, "Failed to clear session from IndexedDB", { error: request.error });
        reject(request.error);
      };
    });
  }
  async getShouldClearPreviousSession() {
    debug.log(DebugCategory.STORAGE, "Getting shouldClearPreviousSession flag from IndexedDB");
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);
      const request = store.get("shouldClearPreviousSession");
      request.onsuccess = () => {
        const shouldClear = request.result ?? false;
        debug.log(DebugCategory.STORAGE, "Retrieved shouldClearPreviousSession flag from IndexedDB", {
          shouldClear
        });
        resolve(shouldClear);
      };
      request.onerror = () => {
        debug.error(DebugCategory.STORAGE, "Failed to get shouldClearPreviousSession flag from IndexedDB", {
          error: request.error
        });
        reject(request.error);
      };
    });
  }
  async setShouldClearPreviousSession(should) {
    debug.log(DebugCategory.STORAGE, "Setting shouldClearPreviousSession flag in IndexedDB", { should });
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.put(should, "shouldClearPreviousSession");
      request.onsuccess = () => {
        debug.log(DebugCategory.STORAGE, "Successfully set shouldClearPreviousSession flag in IndexedDB");
        resolve();
      };
      request.onerror = () => {
        debug.error(DebugCategory.STORAGE, "Failed to set shouldClearPreviousSession flag in IndexedDB", {
          error: request.error
        });
        reject(request.error);
      };
    });
  }
};

// src/providers/embedded/adapters/url-params.ts
var BrowserURLParamsAccessor = class {
  getParam(key) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(key);
  }
};
var browserUrlParamsAccessor = new BrowserURLParamsAccessor();

// src/providers/embedded/adapters/auth.ts
import { DEFAULT_AUTH_URL } from "@phantom/constants";

// src/utils/browser-detection.ts
function parseBrowserFromUserAgent(userAgent, hasBraveAPI) {
  let name = "unknown";
  let version = "unknown";
  if (!userAgent || typeof userAgent !== "string") {
    return { name, version, userAgent: "unknown" };
  }
  try {
    if (userAgent.includes("Edg/")) {
      name = "edge";
      const match = userAgent.match(/Edg\/([0-9]+(?:\.[0-9]+)*)/);
      if (match)
        version = match[1].split(".")[0];
    } else if (userAgent.includes("OPR/") || userAgent.includes("Opera/")) {
      name = "opera";
      const match = userAgent.match(/(?:OPR|Opera)\/([0-9]+(?:\.[0-9]+)*)/);
      if (match)
        version = match[1].split(".")[0];
    } else if (userAgent.includes("SamsungBrowser/")) {
      name = "samsung";
      const match = userAgent.match(/SamsungBrowser\/([0-9]+(?:\.[0-9]+)*)/);
      if (match)
        version = match[1].split(".")[0];
    } else if (userAgent.includes("DuckDuckGo/")) {
      name = "duckduckgo";
      const match = userAgent.match(/DuckDuckGo\/([0-9]+(?:\.[0-9]+)*)/);
      if (match)
        version = match[1].split(".")[0];
    } else if (userAgent.includes("Chrome/") && hasBraveAPI) {
      name = "brave";
      const match = userAgent.match(/Chrome\/([0-9]+(?:\.[0-9]+)*)/);
      if (match)
        version = match[1].split(".")[0];
    } else if (userAgent.includes("Mobile/") || userAgent.includes("Android")) {
      if (userAgent.includes("Chrome/")) {
        name = "chrome-mobile";
        const match = userAgent.match(/Chrome\/([0-9]+(?:\.[0-9]+)*)/);
        if (match)
          version = match[1].split(".")[0];
      } else if (userAgent.includes("Firefox/")) {
        name = "firefox-mobile";
        const match = userAgent.match(/Firefox\/([0-9]+(?:\.[0-9]+)*)/);
        if (match)
          version = match[1].split(".")[0];
      } else if (userAgent.includes("Safari/") && userAgent.includes("Mobile/")) {
        name = "safari-mobile";
        const match = userAgent.match(/Version\/([0-9]+(?:\.[0-9]+)*)/);
        if (match)
          version = match[1].split(".")[0];
      } else {
        name = "mobile";
      }
    } else if (userAgent.includes("Chrome/")) {
      name = "chrome";
      const match = userAgent.match(/Chrome\/([0-9]+(?:\.[0-9]+)*)/);
      if (match)
        version = match[1].split(".")[0];
    } else if (userAgent.includes("Firefox/")) {
      name = "firefox";
      const match = userAgent.match(/Firefox\/([0-9]+(?:\.[0-9]+)*)/);
      if (match)
        version = match[1].split(".")[0];
    } else if (userAgent.includes("Safari/") && !userAgent.includes("Chrome/")) {
      name = "safari";
      const match = userAgent.match(/Version\/([0-9]+(?:\.[0-9]+)*)/);
      if (match)
        version = match[1].split(".")[0];
    }
    if (name === "unknown") {
      const patterns = [
        { regex: /Chrome\/([0-9]+)/, name: "chrome" },
        { regex: /Firefox\/([0-9]+)/, name: "firefox" },
        { regex: /Safari\/([0-9]+)/, name: "safari" },
        { regex: /Edge\/([0-9]+)/, name: "edge" },
        { regex: /Opera\/([0-9]+)/, name: "opera" }
      ];
      for (const pattern of patterns) {
        const match = userAgent.match(pattern.regex);
        if (match) {
          name = pattern.name;
          version = match[1];
          break;
        }
      }
    }
  } catch (error) {
  }
  return { name, version, userAgent };
}
function detectBrowser() {
  if (typeof window === "undefined" || !window.navigator?.userAgent) {
    return { name: "unknown", version: "unknown", userAgent: "unknown" };
  }
  const userAgent = window.navigator.userAgent;
  const hasBraveAPI = !!navigator.brave;
  return parseBrowserFromUserAgent(userAgent, hasBraveAPI);
}
function getPlatformName() {
  const { name, version } = detectBrowser();
  return version !== "unknown" ? `${name}-v${version}` : name;
}
function getBrowserDisplayName() {
  const { name, version } = detectBrowser();
  const capitalizedName = name.charAt(0).toUpperCase() + name.slice(1);
  return version !== "unknown" ? `${capitalizedName} ${version}` : capitalizedName;
}
function isMobileDevice() {
  if (typeof window === "undefined" || !window.navigator?.userAgent) {
    return false;
  }
  const userAgent = window.navigator.userAgent.toLowerCase();
  const mobilePatterns = [
    /android/,
    /iphone|ipad|ipod/,
    /blackberry/,
    /windows phone/,
    /mobile/,
    /tablet/,
    /silk/,
    /kindle/,
    /opera mini/,
    /opera mobi/
  ];
  const isMobileUA = mobilePatterns.some((pattern) => pattern.test(userAgent));
  let isSmallScreen = false;
  try {
    isSmallScreen = window.screen.width <= 768 || window.screen.height <= 768;
  } catch (error) {
    isSmallScreen = false;
  }
  let isTouchDevice = false;
  try {
    isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  } catch (error) {
    isTouchDevice = false;
  }
  return isMobileUA || isSmallScreen && isTouchDevice;
}

// src/providers/embedded/adapters/auth.ts
var BrowserAuthProvider = class {
  constructor(urlParamsAccessor) {
    this.urlParamsAccessor = urlParamsAccessor;
  }
  getValidatedCurrentUrl() {
    const currentUrl = window.location.href;
    if (!currentUrl.startsWith("http:") && !currentUrl.startsWith("https:")) {
      throw new Error("Invalid URL protocol - only HTTP/HTTPS URLs are supported");
    }
    return currentUrl;
  }
  authenticate(options) {
    return new Promise((resolve) => {
      if ("jwtToken" in options) {
        throw new Error("JWT authentication should be handled by the core JWTAuth class");
      }
      const phantomOptions = options;
      debug.info(DebugCategory.PHANTOM_CONNECT_AUTH, "Starting Phantom Connect authentication", {
        publicKey: phantomOptions.publicKey,
        appId: phantomOptions.appId,
        provider: phantomOptions.provider,
        authUrl: phantomOptions.authUrl
      });
      const baseUrl = phantomOptions.authUrl || DEFAULT_AUTH_URL;
      debug.log(DebugCategory.PHANTOM_CONNECT_AUTH, "Using auth URL", { baseUrl });
      const params = new URLSearchParams({
        public_key: phantomOptions.publicKey,
        app_id: phantomOptions.appId,
        redirect_uri: phantomOptions.redirectUrl || (typeof window !== "undefined" ? this.getValidatedCurrentUrl() : ""),
        session_id: phantomOptions.sessionId,
        // OAuth session management - defaults to allow refresh unless explicitly clearing after logout
        clear_previous_session: (phantomOptions.clearPreviousSession ?? false).toString(),
        allow_refresh: (phantomOptions.allowRefresh ?? true).toString(),
        sdk_version: "1.0.2",
        sdk_type: "browser",
        platform: detectBrowser().name
      });
      if (phantomOptions.provider) {
        debug.log(DebugCategory.PHANTOM_CONNECT_AUTH, "Provider specified, will skip selection", {
          provider: phantomOptions.provider
        });
        params.append("provider", phantomOptions.provider);
      } else {
        debug.log(DebugCategory.PHANTOM_CONNECT_AUTH, "No provider specified, defaulting to Google");
        params.append("provider", "google");
      }
      const authContext = {
        publicKey: phantomOptions.publicKey,
        appId: phantomOptions.appId,
        provider: phantomOptions.provider,
        sessionId: phantomOptions.sessionId
      };
      sessionStorage.setItem("phantom-auth-context", JSON.stringify(authContext));
      debug.log(DebugCategory.PHANTOM_CONNECT_AUTH, "Stored auth context in session storage", { authContext });
      const authUrl = `${baseUrl}?${params.toString()}`;
      debug.info(DebugCategory.PHANTOM_CONNECT_AUTH, "Redirecting to Phantom Connect", { authUrl });
      if (!authUrl.startsWith("https:") && !authUrl.startsWith("http://localhost")) {
        throw new Error("Invalid auth URL - only HTTPS URLs are allowed for authentication");
      }
      window.location.href = authUrl;
      resolve();
    });
  }
  resumeAuthFromRedirect(provider) {
    try {
      const walletId = this.urlParamsAccessor.getParam("wallet_id");
      const sessionId = this.urlParamsAccessor.getParam("session_id");
      const accountDerivationIndex = this.urlParamsAccessor.getParam("selected_account_index");
      const error = this.urlParamsAccessor.getParam("error");
      const errorDescription = this.urlParamsAccessor.getParam("error_description");
      if (error) {
        const errorMsg = errorDescription || error;
        switch (error) {
          case "access_denied":
            throw new Error(`Authentication cancelled: ${errorMsg}`);
          case "invalid_request":
            throw new Error(`Invalid authentication request: ${errorMsg}`);
          case "server_error":
            throw new Error(`Authentication server error: ${errorMsg}`);
          case "temporarily_unavailable":
            throw new Error(`Authentication service temporarily unavailable: ${errorMsg}`);
          default:
            throw new Error(`Authentication failed: ${errorMsg}`);
        }
      }
      if (!walletId || !sessionId) {
        debug.log(DebugCategory.PHANTOM_CONNECT_AUTH, "Missing auth parameters in URL", {
          hasWalletId: !!walletId,
          hasSessionId: !!sessionId
        });
        return null;
      }
      const contextStr = sessionStorage.getItem("phantom-auth-context");
      let context = {};
      if (contextStr) {
        try {
          context = JSON.parse(contextStr);
        } catch (parseError) {
          debug.warn(DebugCategory.PHANTOM_CONNECT_AUTH, "Failed to parse stored auth context", { error: parseError });
        }
      }
      if (context.sessionId && sessionId !== context.sessionId) {
        debug.error(DebugCategory.PHANTOM_CONNECT_AUTH, "Session ID mismatch", {
          urlSessionId: sessionId,
          storedSessionId: context.sessionId
        });
        throw new Error("Session ID mismatch - possible session corruption or replay attack");
      }
      sessionStorage.removeItem("phantom-auth-context");
      debug.info(DebugCategory.PHANTOM_CONNECT_AUTH, "Successfully resumed auth from redirect", {
        walletId,
        sessionId,
        accountDerivationIndex: accountDerivationIndex ? parseInt(accountDerivationIndex) : void 0
      });
      const organizationId = this.urlParamsAccessor.getParam("organization_id");
      const expiresInMs = this.urlParamsAccessor.getParam("expires_in_ms");
      const authUserId = this.urlParamsAccessor.getParam("auth_user_id");
      debug.log(DebugCategory.PHANTOM_CONNECT_AUTH, "Auth redirect parameters", {
        walletId,
        organizationId,
        sessionId,
        accountDerivationIndex,
        expiresInMs,
        authUserId
      });
      if (!organizationId) {
        debug.error(DebugCategory.PHANTOM_CONNECT_AUTH, "Missing organization_id in auth response");
        throw new Error("Missing organization_id in auth response");
      }
      if (organizationId.startsWith("temp-")) {
        debug.warn(
          DebugCategory.PHANTOM_CONNECT_AUTH,
          "Received temporary organization_id, server may not be configured properly",
          {
            organizationId
          }
        );
      }
      return {
        walletId,
        organizationId,
        accountDerivationIndex: accountDerivationIndex ? parseInt(accountDerivationIndex) : 0,
        expiresInMs: expiresInMs ? parseInt(expiresInMs) : 0,
        authUserId: authUserId || void 0,
        provider
      };
    } catch (error) {
      sessionStorage.removeItem("phantom-auth-context");
      throw error;
    }
  }
};

// src/providers/embedded/adapters/phantom-app.ts
import { isPhantomExtensionInstalled as isPhantomExtensionInstalled3 } from "@phantom/browser-injected-sdk";

// src/isPhantomLoginAvailable.ts
import { isPhantomExtensionInstalled as isPhantomExtensionInstalled2 } from "@phantom/browser-injected-sdk";
async function isPhantomLoginAvailable(timeoutMs = 3e3) {
  const extensionInstalled = await waitForExtension(timeoutMs);
  if (!extensionInstalled) {
    return false;
  }
  try {
    if (!window.phantom?.app?.features || typeof window.phantom.app.features !== "function") {
      return false;
    }
    const response = await window.phantom.app.features();
    if (!Array.isArray(response.features)) {
      return false;
    }
    return response.features.includes("phantom_login");
  } catch (error) {
    console.error("Error checking Phantom extension features", error);
    return false;
  }
}
async function waitForExtension(timeoutMs) {
  return new Promise((resolve) => {
    const startTime = Date.now();
    const checkInterval = 100;
    const checkForExtension = () => {
      try {
        if (isPhantomExtensionInstalled2()) {
          resolve(true);
          return;
        }
      } catch (error) {
      }
      const elapsed = Date.now() - startTime;
      if (elapsed >= timeoutMs) {
        resolve(false);
        return;
      }
      setTimeout(checkForExtension, checkInterval);
    };
    checkForExtension();
  });
}

// src/providers/embedded/adapters/phantom-app.ts
var BrowserPhantomAppProvider = class {
  /**
   * Check if the Phantom extension is installed in the browser
   */
  isAvailable() {
    return isPhantomExtensionInstalled3();
  }
  /**
   * Authenticate using the Phantom browser extension
   */
  async authenticate(options) {
    if (!this.isAvailable()) {
      throw new Error(
        "Phantom extension is not installed. Please install the Phantom browser extension to use this authentication method."
      );
    }
    const loginAvailable = await isPhantomLoginAvailable();
    if (!loginAvailable) {
      throw new Error(
        "Phantom Login is not available. Please update your Phantom extension to use this authentication method."
      );
    }
    try {
      if (!window.phantom?.app?.login) {
        throw new Error("Phantom extension login method not found");
      }
      const result = await window.phantom.app.login({
        publicKey: options.publicKey,
        appId: options.appId,
        sessionId: options.sessionId
      });
      if (!result || !result.walletId || !result.organizationId) {
        throw new Error("Invalid authentication response from Phantom extension");
      }
      return {
        walletId: result.walletId,
        organizationId: result.organizationId,
        provider: "phantom",
        accountDerivationIndex: result.accountDerivationIndex ?? 0,
        expiresInMs: result.expiresInMs ?? 0,
        authUserId: result.authUserId
      };
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Phantom extension authentication failed: ${String(error)}`);
    }
  }
};

// src/providers/embedded/adapters/logger.ts
var BrowserLogger = class {
  info(category, message, data) {
    debug.info(category, message, data);
  }
  warn(category, message, data) {
    debug.warn(category, message, data);
  }
  error(category, message, data) {
    debug.error(category, message, data);
  }
  log(category, message, data) {
    debug.log(category, message, data);
  }
};

// src/providers/embedded/index.ts
import { ANALYTICS_HEADERS } from "@phantom/constants";
var EmbeddedProvider = class extends CoreEmbeddedProvider {
  constructor(config) {
    debug.log(DebugCategory.EMBEDDED_PROVIDER, "Initializing Browser EmbeddedProvider", { config });
    const urlParamsAccessor = new BrowserURLParamsAccessor();
    const stamper = new IndexedDbStamper({
      dbName: `phantom-embedded-sdk-${config.appId}`,
      storeName: "crypto-keys",
      keyName: "signing-key"
    });
    const platformName = getPlatformName();
    const { name: browserName, version } = detectBrowser();
    const platform = {
      storage: new BrowserStorage(),
      authProvider: new BrowserAuthProvider(urlParamsAccessor),
      phantomAppProvider: new BrowserPhantomAppProvider(),
      urlParamsAccessor,
      stamper,
      name: platformName,
      // Use detected browser name and version for identification
      analyticsHeaders: {
        [ANALYTICS_HEADERS.SDK_TYPE]: "browser",
        [ANALYTICS_HEADERS.PLATFORM]: browserName,
        // firefox, chrome, safari, etc.
        [ANALYTICS_HEADERS.PLATFORM_VERSION]: version,
        // Full user agent for more detailed info
        [ANALYTICS_HEADERS.APP_ID]: config.appId,
        [ANALYTICS_HEADERS.WALLET_TYPE]: config.embeddedWalletType,
        [ANALYTICS_HEADERS.SDK_VERSION]: "1.0.2"
        // Replaced at build time
      }
    };
    debug.log(DebugCategory.EMBEDDED_PROVIDER, "Detected platform", { platformName });
    const logger = new BrowserLogger();
    super(config, platform, logger);
    this.addressTypes = config.addressTypes;
    debug.info(DebugCategory.EMBEDDED_PROVIDER, "Browser EmbeddedProvider initialized");
  }
  getEnabledAddressTypes() {
    return this.addressTypes;
  }
};

// src/ProviderManager.ts
import {
  EMBEDDED_PROVIDER_AUTH_TYPES
} from "@phantom/embedded-provider-core";
import { DEFAULT_WALLET_API_URL, DEFAULT_EMBEDDED_WALLET_TYPE, DEFAULT_AUTH_URL as DEFAULT_AUTH_URL2 } from "@phantom/constants";

// src/utils/auth-callback.ts
function isAuthFailureCallback(searchParams) {
  if (typeof window === "undefined" && !searchParams)
    return false;
  const params = searchParams || new URLSearchParams(window.location.search);
  const responseType = params.get("response_type");
  const sessionId = params.get("session_id");
  return responseType === "failure" && !!sessionId;
}
function isAuthCallbackUrl(searchParams) {
  if (typeof window === "undefined" && !searchParams)
    return false;
  const params = searchParams || new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");
  return !!(sessionId && (params.has("response_type") || params.has("wallet_id")));
}

// src/utils/deeplink.ts
function getDeeplinkToPhantom(ref) {
  if (!window.location.href.startsWith("http:") && !window.location.href.startsWith("https:")) {
    throw new Error("Invalid URL protocol - only HTTP/HTTPS URLs are supported for deeplinks");
  }
  const currentUrl = encodeURIComponent(window.location.href);
  const refParam = ref ? `?ref=${encodeURIComponent(ref)}` : "";
  return `https://phantom.app/ul/browse/${currentUrl}${refParam}`;
}

// src/ProviderManager.ts
var ProviderManager = class {
  // Track which providers have forwarding set up
  constructor(config) {
    this.providers = /* @__PURE__ */ new Map();
    this.currentProvider = null;
    this.currentProviderKey = null;
    // Event management for forwarding provider events
    this.eventListeners = /* @__PURE__ */ new Map();
    this.providerForwardingSetup = /* @__PURE__ */ new WeakSet();
    debug.log(DebugCategory.PROVIDER_MANAGER, "Initializing ProviderManager", { config });
    this.config = config;
    debug.log(DebugCategory.PROVIDER_MANAGER, "Setting default provider");
    this.setDefaultProvider();
    debug.info(DebugCategory.PROVIDER_MANAGER, "ProviderManager initialized", {
      currentProviderKey: this.currentProviderKey
    });
  }
  getValidatedCurrentUrl() {
    if (typeof window === "undefined")
      return "";
    const currentUrl = window.location.href;
    if (!currentUrl.startsWith("http:") && !currentUrl.startsWith("https:")) {
      throw new Error("Invalid URL protocol - only HTTP/HTTPS URLs are supported");
    }
    return currentUrl;
  }
  /**
   * Switch to a different provider type
   */
  switchProvider(type, options) {
    if (options?.embeddedWalletType && !["app-wallet", "user-wallet"].includes(options.embeddedWalletType)) {
      throw new Error(
        `Invalid embeddedWalletType: ${options.embeddedWalletType}. Must be "app-wallet" or "user-wallet".`
      );
    }
    const key = this.getProviderKey(type, options?.embeddedWalletType);
    if (!this.providers.has(key)) {
      this.createProvider(type, options?.embeddedWalletType);
    }
    this.currentProvider = this.providers.get(key);
    this.currentProviderKey = key;
    this.ensureProviderEventForwarding();
    return this.currentProvider;
  }
  /**
   * Get the current active provider
   */
  getCurrentProvider() {
    return this.currentProvider;
  }
  /**
   * Get current provider type and options
   */
  getCurrentProviderInfo() {
    if (!this.currentProviderKey)
      return null;
    const parts = this.currentProviderKey.split("-");
    const [type, embeddedWalletType] = parts;
    return {
      type,
      embeddedWalletType
    };
  }
  /**
   * Check if a provider is allowed by the config
   */
  isProviderAllowed(provider) {
    return this.config.providers.includes(provider);
  }
  /**
   * Connect using the current provider
   * Automatically switches provider based on authOptions.provider
   */
  async connect(authOptions) {
    debug.info(DebugCategory.PROVIDER_MANAGER, "Starting connection", {
      currentProviderKey: this.currentProviderKey,
      authOptions: { provider: authOptions.provider }
    });
    if (!this.isProviderAllowed(authOptions.provider)) {
      const error = `Provider "${authOptions.provider}" is not in the allowed providers list: ${JSON.stringify(this.config.providers)}`;
      debug.error(DebugCategory.PROVIDER_MANAGER, error);
      throw new Error(error);
    }
    const requestedProvider = authOptions.provider;
    let targetProviderType = null;
    if (requestedProvider === "injected") {
      targetProviderType = "injected";
    } else if (requestedProvider === "deeplink") {
      try {
        const deeplinkUrl = getDeeplinkToPhantom();
        if (typeof window !== "undefined") {
          window.location.href = deeplinkUrl;
        }
        return {
          addresses: [],
          walletId: void 0,
          authUserId: void 0
        };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to open deeplink";
        debug.error(DebugCategory.PROVIDER_MANAGER, "Deeplink error", { error: errorMessage });
        throw new Error(`Failed to open deeplink: ${errorMessage}`);
      }
    } else if (EMBEDDED_PROVIDER_AUTH_TYPES.includes(requestedProvider)) {
      targetProviderType = "embedded";
    }
    if (targetProviderType) {
      const currentInfo = this.getCurrentProviderInfo();
      if (currentInfo?.type !== targetProviderType) {
        debug.log(DebugCategory.PROVIDER_MANAGER, "Auto-switching provider based on auth options", {
          from: currentInfo?.type,
          to: targetProviderType,
          requestedProvider
        });
        const switchOptions = {};
        if (targetProviderType === "embedded") {
          switchOptions.embeddedWalletType = currentInfo?.embeddedWalletType || this.config.embeddedWalletType;
        }
        this.switchProvider(targetProviderType, switchOptions);
      }
    }
    if (!this.currentProvider) {
      debug.error(DebugCategory.PROVIDER_MANAGER, "No provider selected");
      throw new Error("No provider selected");
    }
    debug.log(DebugCategory.PROVIDER_MANAGER, "Delegating to provider connect method");
    const result = await this.currentProvider.connect(authOptions);
    debug.log(DebugCategory.PROVIDER_MANAGER, "Connection successful, saving preferences", {
      addressCount: result.addresses?.length || 0,
      provider: authOptions.provider
    });
    this.saveProviderPreference();
    debug.info(DebugCategory.PROVIDER_MANAGER, "Connect completed", {
      addresses: result.addresses,
      provider: authOptions.provider
    });
    return result;
  }
  /**
   * Disconnect from current provider
   */
  async disconnect() {
    if (!this.currentProvider)
      return;
    await this.currentProvider.disconnect();
  }
  /**
   * Get addresses from current provider
   */
  getAddresses() {
    if (!this.currentProvider) {
      return [];
    }
    return this.currentProvider.getAddresses();
  }
  /**
   * Check if current provider is connected
   */
  isConnected() {
    return this.currentProvider?.isConnected() ?? false;
  }
  /**
   * Attempt auto-connect with fallback strategy
   * Tries embedded provider first if it exists and is allowed, then injected provider if allowed
   * Returns true if any provider successfully connected
   */
  async autoConnect() {
    debug.log(DebugCategory.PROVIDER_MANAGER, "Starting auto-connect with fallback strategy");
    if (isAuthFailureCallback()) {
      debug.warn(DebugCategory.PROVIDER_MANAGER, "Auth failure detected in URL, skipping autoConnect fallback");
      return false;
    }
    const embeddedWalletType = this.config.embeddedWalletType || "user-wallet";
    const embeddedKey = this.getProviderKey("embedded", embeddedWalletType);
    const embeddedAllowed = this.config.providers.some((p) => p !== "injected");
    if (embeddedAllowed && this.providers.has(embeddedKey)) {
      debug.log(DebugCategory.PROVIDER_MANAGER, "Trying auto-connect with existing embedded provider");
      const embeddedProvider = this.providers.get(embeddedKey);
      try {
        const previousProvider = this.currentProvider;
        const previousKey = this.currentProviderKey;
        this.currentProvider = embeddedProvider;
        this.currentProviderKey = embeddedKey;
        this.ensureProviderEventForwarding();
        await embeddedProvider.autoConnect();
        if (embeddedProvider.isConnected()) {
          debug.info(DebugCategory.PROVIDER_MANAGER, "Embedded auto-connect successful");
          this.saveProviderPreference();
          return true;
        } else {
          debug.log(DebugCategory.PROVIDER_MANAGER, "Embedded provider did not connect, restoring previous provider");
          this.currentProvider = previousProvider;
          this.currentProviderKey = previousKey;
        }
      } catch (error) {
        debug.log(DebugCategory.PROVIDER_MANAGER, "Embedded auto-connect failed", {
          error: error.message
        });
        if (isAuthCallbackUrl()) {
          debug.log(DebugCategory.PROVIDER_MANAGER, "In auth callback URL, not attempting injected fallback");
          return false;
        }
      }
    }
    const injectedAllowed = this.config.providers.includes("injected");
    const injectedKey = this.getProviderKey("injected");
    if (injectedAllowed && this.providers.has(injectedKey)) {
      debug.log(DebugCategory.PROVIDER_MANAGER, "Trying auto-connect with existing injected provider");
      const injectedProvider = this.providers.get(injectedKey);
      try {
        this.currentProvider = injectedProvider;
        this.currentProviderKey = injectedKey;
        this.ensureProviderEventForwarding();
        await injectedProvider.autoConnect();
        if (injectedProvider.isConnected()) {
          debug.info(DebugCategory.PROVIDER_MANAGER, "Injected auto-connect successful");
          this.saveProviderPreference();
          return true;
        }
      } catch (error) {
        debug.log(DebugCategory.PROVIDER_MANAGER, "Injected auto-connect failed", {
          error: error.message
        });
      }
    }
    debug.log(DebugCategory.PROVIDER_MANAGER, "Auto-connect failed for all allowed providers");
    return false;
  }
  /**
   * Add event listener - stores callback and ensures current provider forwards events to ProviderManager
   */
  on(event, callback) {
    debug.log(DebugCategory.PROVIDER_MANAGER, "Adding event listener", { event });
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, /* @__PURE__ */ new Set());
    }
    this.eventListeners.get(event)?.add(callback);
    this.ensureProviderEventForwarding();
  }
  /**
   * Remove event listener
   */
  off(event, callback) {
    debug.log(DebugCategory.PROVIDER_MANAGER, "Removing event listener", { event });
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event)?.delete(callback);
      if (this.eventListeners.get(event)?.size === 0) {
        this.eventListeners.delete(event);
      }
    }
  }
  /**
   * Emit event to all registered callbacks
   */
  emit(event, data) {
    debug.log(DebugCategory.PROVIDER_MANAGER, "Emitting event to stored callbacks", {
      event,
      listenerCount: this.eventListeners.get(event)?.size || 0,
      data
    });
    const listeners = this.eventListeners.get(event);
    if (listeners && listeners.size > 0) {
      listeners.forEach((callback) => {
        try {
          debug.log(DebugCategory.PROVIDER_MANAGER, "Calling stored callback for event", { event });
          callback(data);
        } catch (error) {
          debug.error(DebugCategory.PROVIDER_MANAGER, "Event callback error", { event, error });
        }
      });
    } else {
      debug.warn(DebugCategory.PROVIDER_MANAGER, "No stored callbacks for event", { event });
    }
  }
  /**
   * Ensure current provider forwards its events to this ProviderManager
   * Only sets up forwarding once per provider instance to avoid accumulation
   */
  ensureProviderEventForwarding() {
    if (!this.currentProvider || !("on" in this.currentProvider)) {
      debug.warn(DebugCategory.PROVIDER_MANAGER, "Current provider does not support events", {
        providerType: this.getCurrentProviderInfo()?.type
      });
      return;
    }
    if (this.providerForwardingSetup.has(this.currentProvider)) {
      debug.log(DebugCategory.PROVIDER_MANAGER, "Event forwarding already set up for current provider");
      return;
    }
    debug.log(DebugCategory.PROVIDER_MANAGER, "Setting up event forwarding from current provider");
    const eventsToForward = [
      "connect_start",
      "connect",
      "connect_error",
      "disconnect",
      "error",
      "spending_limit_reached"
    ];
    for (const event of eventsToForward) {
      const forwardingCallback = (data) => {
        debug.log(DebugCategory.PROVIDER_MANAGER, "Forwarding event from provider", { event, data });
        this.emit(event, data);
      };
      debug.log(DebugCategory.PROVIDER_MANAGER, "Attaching forwarding callback for event", { event });
      this.currentProvider.on(event, forwardingCallback);
    }
    this.providerForwardingSetup.add(this.currentProvider);
  }
  /**
   * Set default provider based on initial config
   * Creates providers based on the allowed providers array
   */
  setDefaultProvider() {
    const defaultEmbeddedType = this.config.embeddedWalletType || "user-wallet";
    const hasInjected = this.config.providers.includes("injected");
    const hasEmbedded = this.config.providers.some((p) => p !== "injected" && p !== "deeplink");
    if (hasInjected) {
      debug.log(DebugCategory.PROVIDER_MANAGER, "Creating injected provider (allowed by providers array)");
      this.createProvider("injected");
    }
    if (hasEmbedded) {
      debug.log(DebugCategory.PROVIDER_MANAGER, "Creating embedded provider (allowed by providers array)");
      this.createProvider("embedded", defaultEmbeddedType);
    }
    let defaultType;
    if (hasEmbedded && this.providers.has(`embedded-${defaultEmbeddedType}`)) {
      defaultType = "embedded";
    } else if (hasInjected && this.providers.has("injected")) {
      defaultType = "injected";
    } else {
      throw new Error("No valid providers could be created from the providers array");
    }
    const switchOptions = {};
    if (defaultType === "embedded") {
      switchOptions.embeddedWalletType = defaultEmbeddedType;
    }
    this.switchProvider(defaultType, switchOptions);
  }
  /**
   * Create a provider instance
   */
  createProvider(type, embeddedWalletType) {
    const key = this.getProviderKey(type, embeddedWalletType);
    if (this.providers.has(key))
      return;
    let provider;
    if (type === "injected") {
      provider = new InjectedProvider({
        addressTypes: this.config.addressTypes || [AddressType.solana]
      });
    } else if (type === "embedded") {
      if (!this.config.appId) {
        throw new Error("appId is required for embedded provider");
      }
      const apiBaseUrl = this.config.apiBaseUrl || DEFAULT_WALLET_API_URL;
      const authUrl = this.config.authOptions?.authUrl || DEFAULT_AUTH_URL2;
      provider = new EmbeddedProvider({
        apiBaseUrl,
        appId: this.config.appId,
        authOptions: {
          ...this.config.authOptions || {},
          authUrl,
          redirectUrl: this.config.authOptions?.redirectUrl || this.getValidatedCurrentUrl()
        },
        embeddedWalletType: embeddedWalletType || DEFAULT_EMBEDDED_WALLET_TYPE,
        addressTypes: this.config.addressTypes || [AddressType.solana]
      });
    } else {
      throw new Error(`Unsupported provider type: ${type}`);
    }
    this.providers.set(key, provider);
  }
  /**
   * Generate a unique key for provider instances
   */
  getProviderKey(type, embeddedWalletType) {
    if (type === "injected") {
      return "injected";
    } else if (type === "embedded") {
      return `embedded-${embeddedWalletType || "app-wallet"}`;
    }
    throw new Error(`Unsupported provider type: ${type}`);
  }
  /**
   * Save provider preference to localStorage
   */
  saveProviderPreference() {
    try {
      const preference = this.getCurrentProviderInfo();
      if (preference) {
        localStorage.setItem("phantom-provider-preference", JSON.stringify(preference));
      }
    } catch (error) {
      console.error("Failed to save provider preference:", error);
    }
  }
};

// src/BrowserSDK.ts
import { EMBEDDED_PROVIDER_AUTH_TYPES as EMBEDDED_PROVIDER_AUTH_TYPES2 } from "@phantom/embedded-provider-core";
import { DEFAULT_EMBEDDED_WALLET_TYPE as DEFAULT_EMBEDDED_WALLET_TYPE2 } from "@phantom/constants";
var BROWSER_SDK_PROVIDER_TYPES = [
  ...EMBEDDED_PROVIDER_AUTH_TYPES2,
  "injected",
  "deeplink"
];
var BrowserSDK = class {
  constructor(config) {
    this.walletRegistry = getWalletRegistry();
    this.isLoading = true;
    debug.info(DebugCategory.BROWSER_SDK, "Initializing BrowserSDK", {
      providers: config.providers,
      embeddedWalletType: config.embeddedWalletType,
      addressTypes: config.addressTypes
    });
    if (!Array.isArray(config.providers) || config.providers.length === 0) {
      debug.error(DebugCategory.BROWSER_SDK, "Invalid providers array", { providers: config.providers });
      throw new Error("providers must be a non-empty array of AuthProviderType");
    }
    const invalidProviders = config.providers.filter((p) => !BROWSER_SDK_PROVIDER_TYPES.includes(p));
    if (invalidProviders.length > 0) {
      debug.error(DebugCategory.BROWSER_SDK, "Invalid provider types", {
        invalidProviders,
        validProviders: BROWSER_SDK_PROVIDER_TYPES
      });
      throw new Error(
        `Invalid provider type(s): ${invalidProviders.join(", ")}. Valid providers are: ${BROWSER_SDK_PROVIDER_TYPES.join(", ")}`
      );
    }
    const hasEmbeddedProviders = config.providers.some((p) => p !== "injected");
    if (hasEmbeddedProviders && !config.appId) {
      debug.error(DebugCategory.BROWSER_SDK, "appId required for embedded providers", {
        providers: config.providers
      });
      throw new Error("appId is required when using embedded providers (google, apple, phantom, etc.)");
    }
    const embeddedWalletType = config.embeddedWalletType || DEFAULT_EMBEDDED_WALLET_TYPE2;
    if (!["app-wallet", "user-wallet"].includes(embeddedWalletType)) {
      debug.error(DebugCategory.BROWSER_SDK, "Invalid embeddedWalletType", {
        embeddedWalletType: config.embeddedWalletType
      });
      throw new Error(
        `Invalid embeddedWalletType: ${config.embeddedWalletType}. Must be "app-wallet" or "user-wallet".`
      );
    }
    this.config = config;
    this.providerManager = new ProviderManager(config);
    void this.discoverWallets();
  }
  discoverWallets() {
    return this.walletRegistry.discover(this.config.addressTypes).finally(() => {
      this.isLoading = false;
    });
  }
  // ===== CHAIN API =====
  /**
   * Access Solana chain operations
   */
  get solana() {
    const currentProvider = this.providerManager.getCurrentProvider();
    if (!currentProvider) {
      throw new Error("No provider available. Call connect() first.");
    }
    return currentProvider.solana;
  }
  /**
   * Access Ethereum chain operations
   */
  get ethereum() {
    const currentProvider = this.providerManager.getCurrentProvider();
    if (!currentProvider) {
      throw new Error("No provider available. Call connect() first.");
    }
    return currentProvider.ethereum;
  }
  // ===== CONNECTION MANAGEMENT =====
  /**
   * Connect to the wallet
   */
  async connect(options) {
    debug.info(DebugCategory.BROWSER_SDK, "Starting connection", options);
    try {
      const result = await this.providerManager.connect(options);
      debug.info(DebugCategory.BROWSER_SDK, "Connection successful", {
        addressCount: result.addresses.length,
        status: result.status
      });
      return result;
    } catch (error) {
      debug.error(DebugCategory.BROWSER_SDK, "Connection failed", { error: error.message });
      throw error;
    }
  }
  /**
   * Disconnect from the wallet
   */
  async disconnect() {
    debug.info(DebugCategory.BROWSER_SDK, "Disconnecting");
    try {
      await this.providerManager.disconnect();
      debug.info(DebugCategory.BROWSER_SDK, "Disconnection successful");
    } catch (error) {
      debug.error(DebugCategory.BROWSER_SDK, "Disconnection failed", { error: error.message });
      throw error;
    }
  }
  // ===== STATE QUERIES =====
  /**
   * Check if the SDK is connected to a wallet
   */
  isConnected() {
    return this.providerManager.isConnected();
  }
  /**
   * Get all connected wallet addresses
   */
  getAddresses() {
    return this.providerManager.getAddresses();
  }
  /**
   * Get information about the current provider
   */
  getCurrentProviderInfo() {
    return this.providerManager.getCurrentProviderInfo();
  }
  /**
   * Get enabled address types for the current provider
   * - For embedded provider: returns config.addressTypes
   * - For Phantom injected: returns config.addressTypes
   * - For discovered wallets: returns the wallet's addressTypes from registry
   */
  getEnabledAddressTypes() {
    const currentProvider = this.providerManager.getCurrentProvider();
    if (!currentProvider) {
      return [];
    }
    return currentProvider.getEnabledAddressTypes();
  }
  // ===== UTILITY METHODS =====
  /**
   * Add event listener for provider events (connect, connect_start, connect_error, disconnect, error)
   * Works with both embedded and injected providers
   */
  on(event, callback) {
    debug.log(DebugCategory.BROWSER_SDK, "Adding event listener", { event });
    this.providerManager.on(event, callback);
  }
  /**
   * Remove event listener for provider events
   * Works with both embedded and injected providers
   */
  off(event, callback) {
    debug.log(DebugCategory.BROWSER_SDK, "Removing event listener", { event });
    this.providerManager.off(event, callback);
  }
  /**
   * Attempt auto-connection using existing session
   * Should be called after setting up event listeners
   * Tries embedded provider first, then injected provider as fallback
   */
  async autoConnect() {
    debug.log(DebugCategory.BROWSER_SDK, "Attempting auto-connect with fallback strategy");
    const result = await this.providerManager.autoConnect();
    if (result) {
      debug.info(DebugCategory.BROWSER_SDK, "Auto-connect successful", {
        providerType: this.getCurrentProviderInfo()?.type
      });
    } else {
      debug.log(DebugCategory.BROWSER_SDK, "Auto-connect failed for all providers");
    }
  }
  enableDebug() {
    debug.enable();
    debug.info(DebugCategory.BROWSER_SDK, "Debug logging enabled");
  }
  disableDebug() {
    debug.disable();
  }
  setDebugLevel(level) {
    debug.setLevel(level);
    debug.info(DebugCategory.BROWSER_SDK, "Debug level updated", { level });
  }
  setDebugCallback(callback) {
    debug.setCallback(callback);
    debug.info(DebugCategory.BROWSER_SDK, "Debug callback updated");
  }
  configureDebug(config) {
    if (config.enabled !== void 0) {
      if (config.enabled) {
        this.enableDebug();
      } else {
        this.disableDebug();
      }
    }
    if (config.level !== void 0) {
      this.setDebugLevel(config.level);
    }
    if (config.callback !== void 0) {
      this.setDebugCallback(config.callback);
    }
  }
  // ===== AUTO-CONFIRM METHODS (Injected Provider Only) =====
  /**
   * Enable auto-confirm for transactions
   * Only available for injected providers
   */
  async enableAutoConfirm(params) {
    debug.info(DebugCategory.BROWSER_SDK, "Enabling auto-confirm", { params });
    const currentProvider = this.providerManager.getCurrentProvider();
    if (!currentProvider) {
      throw new Error("No provider available. Call connect() first.");
    }
    if (!("enableAutoConfirm" in currentProvider)) {
      throw new Error("Auto-confirm is only available for injected providers");
    }
    try {
      const result = await currentProvider.enableAutoConfirm(params);
      debug.info(DebugCategory.BROWSER_SDK, "Auto-confirm enabled successfully", { result });
      return result;
    } catch (error) {
      debug.error(DebugCategory.BROWSER_SDK, "Failed to enable auto-confirm", { error: error.message });
      throw error;
    }
  }
  /**
   * Disable auto-confirm for transactions
   * Only available for injected providers
   */
  async disableAutoConfirm() {
    debug.info(DebugCategory.BROWSER_SDK, "Disabling auto-confirm");
    const currentProvider = this.providerManager.getCurrentProvider();
    if (!currentProvider) {
      throw new Error("No provider available. Call connect() first.");
    }
    if (!("disableAutoConfirm" in currentProvider)) {
      throw new Error("Auto-confirm is only available for injected providers");
    }
    try {
      await currentProvider.disableAutoConfirm();
      debug.info(DebugCategory.BROWSER_SDK, "Auto-confirm disabled successfully");
    } catch (error) {
      debug.error(DebugCategory.BROWSER_SDK, "Failed to disable auto-confirm", { error: error.message });
      throw error;
    }
  }
  /**
   * Get current auto-confirm status
   * Only available for injected providers
   */
  async getAutoConfirmStatus() {
    debug.info(DebugCategory.BROWSER_SDK, "Getting auto-confirm status");
    const currentProvider = this.providerManager.getCurrentProvider();
    if (!currentProvider) {
      throw new Error("No provider available. Call connect() first.");
    }
    if (!("getAutoConfirmStatus" in currentProvider)) {
      throw new Error("Auto-confirm is only available for injected providers");
    }
    try {
      const result = await currentProvider.getAutoConfirmStatus();
      debug.info(DebugCategory.BROWSER_SDK, "Got auto-confirm status", { result });
      return result;
    } catch (error) {
      debug.error(DebugCategory.BROWSER_SDK, "Failed to get auto-confirm status", { error: error.message });
      throw error;
    }
  }
  /**
   * Get supported chains for auto-confirm
   * Only available for injected providers
   */
  async getSupportedAutoConfirmChains() {
    debug.info(DebugCategory.BROWSER_SDK, "Getting supported auto-confirm chains");
    const currentProvider = this.providerManager.getCurrentProvider();
    if (!currentProvider) {
      throw new Error("No provider available. Call connect() first.");
    }
    if (!("getSupportedAutoConfirmChains" in currentProvider)) {
      throw new Error("Auto-confirm is only available for injected providers");
    }
    try {
      const result = await currentProvider.getSupportedAutoConfirmChains();
      debug.info(DebugCategory.BROWSER_SDK, "Got supported auto-confirm chains", { result });
      return result;
    } catch (error) {
      debug.error(DebugCategory.BROWSER_SDK, "Failed to get supported auto-confirm chains", {
        error: error.message
      });
      throw error;
    }
  }
  getDiscoveredWallets() {
    debug.log(DebugCategory.BROWSER_SDK, "Getting discovered wallets");
    try {
      const allWallets = this.walletRegistry.getByAddressTypes(this.config.addressTypes);
      debug.log(DebugCategory.BROWSER_SDK, "Retrieved discovered wallets", {
        count: allWallets.length,
        walletIds: allWallets.map((w) => w.id)
      });
      return allWallets;
    } catch (error) {
      debug.error(DebugCategory.BROWSER_SDK, "Failed to get discovered wallets", {
        error: error.message
      });
      return [];
    }
  }
};

// src/waitForPhantomExtension.ts
import { isPhantomExtensionInstalled as isPhantomExtensionInstalled4 } from "@phantom/browser-injected-sdk";
async function waitForPhantomExtension(timeoutMs = 3e3) {
  return new Promise((resolve) => {
    const startTime = Date.now();
    const checkInterval = 100;
    const checkForExtension = () => {
      try {
        if (isPhantomExtensionInstalled4()) {
          resolve(true);
          return;
        }
      } catch (error) {
      }
      const elapsed = Date.now() - startTime;
      if (elapsed >= timeoutMs) {
        resolve(false);
        return;
      }
      setTimeout(checkForExtension, checkInterval);
    };
    checkForExtension();
  });
}

// src/index.ts
import { NetworkId } from "@phantom/constants";
import { AddressType as AddressType3 } from "@phantom/client";
export {
  AddressType3 as AddressType,
  BrowserSDK,
  DebugCategory,
  DebugLevel,
  NetworkId,
  debug,
  detectBrowser,
  getBrowserDisplayName,
  getDeeplinkToPhantom,
  getPlatformName,
  isMobileDevice,
  isPhantomLoginAvailable,
  parseBrowserFromUserAgent,
  waitForPhantomExtension
};
